import React, { useState, useEffect } from 'react';
import { FaTimes, FaExclamationTriangle, FaUpload, FaCalendarAlt, FaBolt, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { doc, getDoc, collection, query, getDocs, where, orderBy, limit } from 'firebase/firestore';
import Swal from 'sweetalert2';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { calcularAdeudoVisibleDesdeObjeto } from '../utils/calcularAdeudoVisible';
import { esConceptoMantenimiento, esConceptoCuotaExtra, esConceptoAgua, esConceptoRecargos, esConceptoMulta, esConceptoSancion, esPartidaContableResidente, puedePartidaGenerarSaldoFavor } from '../utils/conceptosContables';
import './ModalRegistroPago.css';


const isCuotaExtraPagadaCompleta = (cuota, rawData = []) => {
    if (!cuota || !Array.isArray(rawData)) return false;
    const cleanName = String(cuota.descripcion || '').replace(/\s+/g, '').toLowerCase().trim();
    const cuotaIdStr = String(cuota.id || '').trim();

    return rawData.some(m => {
        const mCuotaExtraId = m.cuotaExtraId !== undefined ? String(m.cuotaExtraId).trim() : '';
        const mIdLower = String(m.id || m.mesId || '').toLowerCase();
        const mConceptoLower = String(m.concepto || '').toLowerCase();

        const matchesId = (cuotaIdStr && mCuotaExtraId && cuotaIdStr === mCuotaExtraId);
        const matchesName = cleanName && (
            mIdLower.includes(cleanName) ||
            mConceptoLower.includes(cleanName) ||
            (m.esExtraordinaria && mConceptoLower.includes(cleanName))
        );

        if (!matchesId && !matchesName) return false;

        const est = String(m.estado || '').trim().toLowerCase();
        const esEstadoPagado = ['pagado_completo', 'pagado', 'pagado_totalmente', 'pagado totalmente'].includes(est);
        const esMontoCero = (m.montoPendiente !== undefined && Number(m.montoPendiente) <= 0 && est !== 'pendiente' && est !== 'adeudo');

        return esEstadoPagado || esMontoCero;
    });
};

const reorderOtrosLast = (list = []) => {
    const seen = new Set();
    const cleaned = [];
    let hasOtros = false;
    list.forEach((item) => {
        const norm = (item || '').toString().trim();
        if (!norm) return;
        if (norm.toLowerCase() === 'otros') {
            hasOtros = true;
            return;
        }
        const key = norm.toLowerCase();
        if (!seen.has(key)) {
            seen.add(key);
            cleaned.push(norm);
        }
    });
    if (hasOtros) cleaned.push('Otros');
    return cleaned;
};

const parseMesNumero = (val) => {
    let num = Number(val);
    if (!isNaN(num) && num > 0) return num;
    const nombres = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
    const idx = nombres.indexOf(String(val || '').trim().toLowerCase());
    return idx >= 0 ? idx + 1 : 0;
};

// FALLBACK MATEMÁTICO: Si no hay colección 'mesesPendientes' o falla Firebase.
const aplicarFallbackMatematico = (casa, montoMensual, setPrimerMesDeuda, setMesActualYaPagado) => {
    const deudaTotalCasa = Number(casa?.adeudo || casa?.deudaTotal || casa?.monto || 0);
    const saldoFavor = Number(casa?.saldoFavor || 0);

    if (deudaTotalCasa > 0 && montoMensual > 0 && saldoFavor <= 0) {
        // Asignar el mes más antiguo usando el número de meses de deuda reportados
        const numMesesAdeudo = Number(casa?.mesesAdeudo || 0) || Math.ceil(deudaTotalCasa / montoMensual);
        const fechaCalculo = new Date();
        fechaCalculo.setMonth(fechaCalculo.getMonth() - numMesesAdeudo + 1); // +1 porque el mes actual se incluye en la mora

        setPrimerMesDeuda({
            mes: fechaCalculo.getMonth() + 1,
            año: fechaCalculo.getFullYear()
        });
        setMesActualYaPagado(false);
    } else {
        // Si no hay deuda, la casa está "al corriente" y el pago va para el mes siguiente
        const fechaSiguiente = new Date();
        fechaSiguiente.setMonth(fechaSiguiente.getMonth() + 1);
        setPrimerMesDeuda({
            mes: fechaSiguiente.getMonth() + 1,
            año: fechaSiguiente.getFullYear()
        });
        setMesActualYaPagado(true);
    }
};

const ModalRegistroPago = ({ casa, onClose, onConfirm }) => {
    const { fraccionamientoId } = useAuth();
    const [errorMessage, setErrorMessage] = useState('');
    const [showConfirmClose, setShowConfirmClose] = useState(false);

    const [multasPendientesMonto, setMultasPendientesMonto] = useState(Number(casa?.adeudoMultas || 0));
    const [periodosAguaPendientes, setPeriodosAguaPendientes] = useState([]);
    const [cuotasExtras, setCuotasExtras] = useState([]);
    const [multasAdminList, setMultasAdminList] = useState([]);
    const [sancionesCasaList, setSancionesCasaList] = useState([]);
    const [acordeonSancionesOpen, setAcordeonSancionesOpen] = useState(true);
    const [conceptos, setConceptos] = useState([]);
    const [descuentoConfiguracion, setDescuentoConfiguracion] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loadingConfig, setLoadingConfig] = useState(true);
    const [nextFolio, setNextFolio] = useState('');
    const [nextFolioNumber, setNextFolioNumber] = useState(null);
    const [receiptConfig, setReceiptConfig] = useState({});
    const [diaCorte, setDiaCorte] = useState(null);
    const [cuotaMensual, setCuotaMensual] = useState(0);
    const [interesesEnabled, setInteresesEnabled] = useState(false);
    const [interesesType, setInteresesType] = useState('fixed');
    const [interesesValue, setInteresesValue] = useState(0);
    const [descuentoEnabled, setDescuentoEnabled] = useState(false);
    const [isMultasEnabled, setIsMultasEnabled] = useState(true);
    const [mesActualYaPagado, setMesActualYaPagado] = useState(false);
    const [primerMesDeuda, setPrimerMesDeuda] = useState(null);
    const [mesesPendientesData, setMesesPendientesData] = useState([]);
    const [rawMesesData, setRawMesesData] = useState([]);
    const [financialConfig, setFinancialConfig] = useState({});
    const [moduloSeleccionMesesActivo, setModuloSeleccionMesesActivo] = useState(true);
    const [modalSelectorMesOpen, setModalSelectorMesOpen] = useState(false);
    const [partidaParaSeleccionarMes, setPartidaParaSeleccionarMes] = useState(null);
    const [acordeonManttoOpen, setAcordeonManttoOpen] = useState(true);
    const [acordeonExtrasOpen, setAcordeonExtrasOpen] = useState(true);

    // Deuda y Saldo a Favor dinámicos leídos directamente del backend (Única Fuente de Verdad)
    const adeudoCapitalCasa = Number(casa?.adeudoCapital ?? 0);
    const adeudoInteresesCasa = Number(casa?.adeudoIntereses ?? casa?.adeudoInteres ?? 0);
    const adeudoBaseMant = adeudoCapitalCasa + adeudoInteresesCasa;

    const saldoFavorActual = Number(casa?.saldoFavor || 0);
    const sumaAguaPendiente = Number(casa?.adeudoAgua || 0);
    const sumaExtrasPendientes = Number(casa?.cuotasExtras || 0);
    const sumaMultasPendientes = Number(casa?.adeudoMultas || 0);

    const adeudoTotal = Number(casa?.adeudo || 0);

    // Adeudo consolidado total que incluye capital, intereses, cuotas extraordinarias, multas y agua
    const totalCuotasExtraPendientes = (cuotasExtras || [])
        .filter(c => !isCuotaExtraPagadaCompleta(c, rawMesesData))
        .reduce((sum, c) => sum + Number(c.monto || 0), 0);
    const cuotasExtrasPendientesFinal = Math.max(totalCuotasExtraPendientes, sumaExtrasPendientes);
    const multasPendientesFinal = Math.max(sumaMultasPendientes, multasPendientesMonto);
    const adeudoTotalGlobal = Math.max(
        adeudoTotal,
        Math.max(0, adeudoBaseMant + cuotasExtrasPendientesFinal + multasPendientesFinal + sumaAguaPendiente - saldoFavorActual)
    );

    // Estado para datos generales de la transacción (pago unificado)
    const initialMontoSugerido = Number(casa?.montoSugerido || 0);
    const initialRefText = casa?.notas || (casa?.nombreArchivo ? `Comprobante: ${casa.nombreArchivo}` : '');
    const [montoTotalCap, setMontoTotalCap] = useState(initialMontoSugerido > 0 ? String(initialMontoSugerido) : '');
    const [fechaPagoGeneral, setFechaPagoGeneral] = useState(new Date().toISOString().split('T')[0]);
    const [metodoPagoGeneral, setMetodoPagoGeneral] = useState('Transferencia Bancaria');
    const [referenciaGeneral, setReferenciaGeneral] = useState(initialRefText);
    const [observacionesGeneral, setObservacionesGeneral] = useState(casa?.notas || '');
    const [comprobanteGeneral, setComprobanteGeneral] = useState(null);

    // Estado para múltiples conceptos en el desglose de pago
    const [pagos, setPagos] = useState([{
        id: 1,
        monto: initialMontoSugerido > 0 ? initialMontoSugerido : 0,
        concepto: 'Cuota de Mantenimiento',
        descuento: 0,
        fechaPago: new Date().toISOString().split('T')[0],
        metodoPago: 'Transferencia Bancaria',
        referencia: initialRefText,
        observaciones: casa?.notas || '',
        observacionesAuto: '',   // texto informativo auto-generado (solo lectura en UI)
        comprobante: null,
        multaAdminId: null,
        multaAdminDesc: '',
        cuotaExtraId: null,
        cuotaExtraDesc: '',
        mesSeleccionadoId: null,
        mesSeleccionadoTexto: '',
        mesSeleccionadoMonto: 0
    }]);

    // Lógica para determinar si una cuota aplica este mes según su frecuencia
    const aplicaEsteMes = (mesInicio, frecuencia) => {
        const hoy = new Date();
        const mesActual = hoy.getMonth() + 1; // 1-12
        
        if (!frecuencia || frecuencia === 'monthly' || frecuencia === 'mensual') return true;
        
        // Convertir mesInicio a número si es string (ej: "Mayo" -> 5)
        const mesNombres = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
        let mInicio = Number(mesInicio);
        if (isNaN(mInicio)) {
            mInicio = mesNombres.indexOf(String(mesInicio || '').toLowerCase()) + 1;
        }
        if (mInicio < 1) mInicio = 1;

        if (frecuencia === 'yearly' || frecuencia === 'anual') return mesActual === mInicio;
        
        const diff = (mesActual - mInicio + 12) % 12;
        switch (frecuencia) {
            case 'bimonthly': case 'bimestral': return diff % 2 === 0;
            case 'quarterly': case 'trimestral': return diff % 3 === 0;
            case 'semiannual': case 'semestral': return diff % 6 === 0;
            default: return false;
        }
    };

    // Bloquear scroll del body cuando el modal está abierto
    useEffect(() => {
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prevOverflow;
        };
    }, []);

    // Cargar conceptos y descuento de configuración
    useEffect(() => {
        const loadConfig = async () => {
            try {
                if (!fraccionamientoId) return;

                const refFracc = doc(db, 'fraccionamientos', fraccionamientoId);
                const snap = await getDoc(refFracc);

                if (snap.exists()) {
                    const data = snap.data();
                    const cfg = data.configuracionRecibos || {};

                    // Guardar toda la configuración de recibos
                    setReceiptConfig(cfg);

                    // Cargar configuración financiera
                    const configFinanciera = data.configuracionFinanciera || {};
                    setFinancialConfig(configFinanciera);

                    const modHabilitados = data.modulosHabilitados || {};
                    setModuloSeleccionMesesActivo(modHabilitados.seleccion_meses_pago !== false);

                    const multasEnabled = configFinanciera.multasEnabled ?? true;
                    setIsMultasEnabled(multasEnabled);

                    // Cargar conceptos de recibos (filtrando si multas no están habilitadas)
                    if (cfg.conceptosList) {
                        let filteredConcepts = [...cfg.conceptosList];
                        if (!multasEnabled) {
                            filteredConcepts = filteredConcepts.filter(c => !esConceptoSancion(c));
                        }
                        if (!filteredConcepts.some(c => String(c).toLowerCase().includes('agua'))) {
                            filteredConcepts.unshift('Agua Potable');
                        }
                        if (!filteredConcepts.some(c => String(c).toLowerCase().includes('cuota') && String(c).toLowerCase().includes('extraordinaria'))) {
                            filteredConcepts.splice(1, 0, 'Cuota Extraordinaria');
                        }
                        setConceptos(reorderOtrosLast(filteredConcepts));
                    } else {
                        setConceptos(['Agua Potable', 'Cuota de Mantenimiento', 'Cuota Extraordinaria', 'Otros']);
                    }

                    // Descuento - probar varios nombres de campo
                    const discountPercent = configFinanciera.discountPercent || configFinanciera.descuentoPorcentaje || 0;
                    const discountEnabled = configFinanciera.discountEnabled ?? configFinanciera.descuentoEnabled ?? false;

                    if (discountPercent > 0) {
                        setDescuentoConfiguracion(discountPercent);
                    }
                    // Solo habilitar descuento si está explícitamente habilitado en la configuración
                    setDescuentoEnabled(discountEnabled);

                    // Día de corte
                    if (configFinanciera.corteDia !== undefined && configFinanciera.corteDia !== null) {
                        setDiaCorte(configFinanciera.corteDia);
                    }

                    // Cuota mensual
                    const montoMensual = configFinanciera.montoMensual || configFinanciera.cuotaMensual || 0;
                    if (montoMensual > 0) {
                        setCuotaMensual(montoMensual);
                    }

                    // Intereses config (tipo y tasa)
                    const iEnabled = Boolean(configFinanciera.interesesEnabled);
                    const iType = (configFinanciera.interesesType || configFinanciera.tipoInteres || 'fixed');
                    const iValue = Number(configFinanciera.interesesValue || configFinanciera.tasaInteres || 0);
                    setInteresesEnabled(iEnabled);
                    setInteresesType(String(iType).toLowerCase());
                    setInteresesValue(iValue);

                    // Multas administrativas configuradas
                    const multasList = Array.isArray(configFinanciera.multasAdministrativasList)
                        ? configFinanciera.multasAdministrativasList
                        : Array.isArray(configFinanciera.multasList)
                            ? configFinanciera.multasList.map((m, i) => ({ id: m.id || i, descripcion: m.descripcion || '', monto: Number(m.monto || 0) }))
                            : [];
                    const cleanedMultas = multasList
                        .filter((m) => m && (m.descripcion || '').trim())
                        .map((m, i) => ({ id: m.id ?? i, descripcion: m.descripcion.trim(), monto: Number(m.monto || 0) }));
                    setMultasAdminList(cleanedMultas);

                    // Cuotas extraordinarias configuradas (inicialmente vacías, se cargan dinámicamente de mesesPendientes)
                    setCuotasExtras([]);

                    // Calcular siguiente folio
                    const serie = (cfg.folioSerie || 'R').replace(/[^A-Za-z]/g, '') || 'R';
                    const folioConsecutivo = cfg.folioConsecutivo || 1;

                    // Obtener todos los ingresos para calcular el consecutivo
                    const ingresosRef = collection(db, 'fraccionamientos', fraccionamientoId, 'ingresos');
                    const ingresosQuery = query(ingresosRef);
                    const ingresosSnap = await getDocs(ingresosQuery);
                    const totalIngresos = ingresosSnap.size;

                    const folioNumber = folioConsecutivo + totalIngresos;
                    const folioFormatted = `${serie}${String(folioNumber).padStart(6, '0')}`;
                    setNextFolio(folioFormatted);
                    setNextFolioNumber(folioNumber);

                    // Validar meses pendientes en memoria (evita fallos de índices de Firebase)
                    try {
                        if (casa?.id) {
                            const mesesRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas', casa.id, 'mesesPendientes');
                            // Se obtiene toda la colección para tener acceso a los documentos pagados y pendientes
                            const snapMeses = await getDocs(mesesRef);

                            if (!snapMeses.empty) {
                                const todosLosMeses = snapMeses.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                                setRawMesesData(todosLosMeses); // Guardamos todos para validaciones de duplicados/pagados (Extra fees)

                                // Filtrar las cuotas extraordinarias de mesesPendientes de esta casa que no estén pagadas
                                const mesesExtrasCasa = todosLosMeses.filter(m => {
                                    const esExtra = (m.id?.startsWith('EXT-') || m.tipo === 'cuota_extraordinaria' || m.esExtraordinaria === true);
                                    const est = String(m.estado || '').trim().toLowerCase();
                                    const pagado = ['pagado_completo', 'pagado', 'pagado_totalmente', 'pagado totalmente'].includes(est);
                                    const pendienteMonto = m.montoPendiente !== undefined ? Number(m.montoPendiente) : Number(m.monto || 0);
                                    return esExtra && !pagado && pendienteMonto > 0;
                                });

                                // Mapearlas al formato compatible con el selector del modal
                                const cleanedCuotasExtras = mesesExtrasCasa.map((m, idx) => {
                                    const conceptoLimpio = String(m.concepto || m.descripcion || m.id || '').replace(/^EXT-/, '').trim();
                                    return {
                                        id: m.id || idx,
                                        descripcion: conceptoLimpio,
                                        monto: Number(m.montoPendiente ?? m.monto ?? 0),
                                        montoOriginal: Number(m.montoOriginal ?? m.monto ?? 0),
                                        montoPagado: Number(m.montoPagado || 0),
                                        estado: m.estado || 'pendiente',
                                        esExtraordinaria: true
                                    };
                                });
                                setCuotasExtras(cleanedCuotasExtras);

                                // Ordenar cronológicamente (más antiguo primero)
                                todosLosMeses.sort((a, b) => {
                                    const añoA = Number(a.año || a.anio || 0);
                                    const añoB = Number(b.año || b.anio || 0);
                                    if (añoA !== añoB) return añoA - añoB;
                                    const mesA = parseMesNumero(a.mesNumero || a.mes);
                                    const mesB = parseMesNumero(b.mesNumero || b.mes);
                                    return mesA - mesB;
                                });

                                const mesesRegulares = todosLosMeses.filter(m => {
                                    const isExtra = String(m.concepto || '').toLowerCase().includes('extra') || m.esExtraordinaria;
                                    const isAgua = String(m.tipo || '').toLowerCase() === 'adeudo_agua' || String(m.concepto || '').toLowerCase().includes('agua') || String(m.concepto || '').toLowerCase().includes('cea');
                                    return !isExtra && !isAgua;
                                });

                                const adeudoTotalCalculado = Number(casa?.adeudo || casa?.deudaTotal || casa?.monto || 0);
                                const saldoFavorCalculado = Number(casa?.saldoFavor || 0);
                                const hoy = new Date();

                                // 1. Buscar el mes pendiente más antiguo (con validación robusta)
                                const pendientes = mesesRegulares.filter(m => {
                                    const est = String(m.estado || '').trim().toLowerCase();
                                    const isPendiente = est === 'pendiente' || est === 'adeudo' || est === 'pagado_parcialmente' || est === 'pagado_parcial';
                                    
                                    if (isPendiente && saldoFavorCalculado > 0 && adeudoTotalCalculado <= 0) {
                                        const aNum = Number(m.año || m.anio);
                                        const mNum = parseMesNumero(m.mesNumero || m.mes);
                                        if (aNum < hoy.getFullYear() || (aNum === hoy.getFullYear() && mNum <= hoy.getMonth() + 1)) {
                                            return false; // Registro fantasma neutralizado matemáticamente
                                        }
                                    }
                                    return isPendiente;
                                });

                                setMesesPendientesData(pendientes);

                                if (pendientes.length > 0) {
                                    const primerPendiente = pendientes[0];
                                    setPrimerMesDeuda({
                                        mes: parseMesNumero(primerPendiente.mesNumero || primerPendiente.mes),
                                        año: Number(primerPendiente.año || primerPendiente.anio)
                                    });
                                    setMesActualYaPagado(false);
                                } else {
                                    // 2. Si no hay pendientes, determinar el último mes pagado para saber desde dónde cobrar
                                    const pagados = mesesRegulares.filter(m => {
                                        const est = String(m.estado || '').trim().toLowerCase();
                                        const porEstado = ['pagado_completo', 'pagado', 'pagado_totalmente', 'pagado totalmente'].includes(est);
                                        const porArray = Array.isArray(m.pagos) && m.pagos.length > 0;
                                        let esPagado = porEstado || porArray || !!m.fechaPago || m.esPagoAnticipo === true;
                                        
                                        if (!esPagado && saldoFavorCalculado > 0 && adeudoTotalCalculado <= 0) {
                                            const isExtra = String(m.concepto || '').toLowerCase().includes('extra') || m.esExtraordinaria;
                                            if (!isExtra) {
                                                const aNum = Number(m.año || m.anio);
                                                const mNum = parseMesNumero(m.mesNumero || m.mes);
                                                if (aNum < hoy.getFullYear() || (aNum === hoy.getFullYear() && mNum <= hoy.getMonth() + 1)) {
                                                    esPagado = true; // Forzamos a que cuente como pagado para la proyección
                                                }
                                            }
                                        }
                                        return esPagado;
                                    });

                                    if (pagados.length > 0) {
                                        const ultimoPagado = pagados[pagados.length - 1]; 
                                        const mesUltimo = parseMesNumero(ultimoPagado.mesNumero || ultimoPagado.mes);
                                        const anioUltimo = Number(ultimoPagado.año || ultimoPagado.anio);

                                        let sgtMes = mesUltimo + 1;
                                        let sgtAnio = anioUltimo;
                                        if (sgtMes > 12) {
                                            sgtMes = 1;
                                            sgtAnio += 1;
                                        }

                                        // REGLA MATEMÁTICA DEFINITIVA:
                                        // Si la BD no tiene el documento de este mes (ej. se quedó en Abril), 
                                        // sgtMes calcularía Mayo. Pero si hay Saldo a Favor, Mayo ya está cubierto!
                                        if (saldoFavorCalculado > 0 && adeudoTotalCalculado <= 0) {
                                            const mesActual = hoy.getMonth() + 1;
                                            const anioActual = hoy.getFullYear();
                                            if (sgtAnio < anioActual || (sgtAnio === anioActual && sgtMes <= mesActual)) {
                                                sgtMes = mesActual + 1;
                                                sgtAnio = anioActual;
                                                if (sgtMes > 12) {
                                                    sgtMes = 1;
                                                    sgtAnio += 1;
                                                }
                                            }
                                        }

                                        setPrimerMesDeuda({ mes: sgtMes, año: sgtAnio });
                                        setMesActualYaPagado(false);
                                    } else {
                                        aplicarFallbackMatematico(casa, montoMensual, setPrimerMesDeuda, setMesActualYaPagado);
                                    }
                                }
                            } else {
                                aplicarFallbackMatematico(casa, montoMensual, setPrimerMesDeuda, setMesActualYaPagado);
                                setMesesPendientesData([]);
                            }
                        }
                    } catch (errMeses) {
                        console.warn('No se pudieron cargar mesesPendientes, aplicando fallback:', errMeses);
                        aplicarFallbackMatematico(casa, montoMensual, setPrimerMesDeuda, setMesActualYaPagado);
                    }
                    // Cargar consumos de agua pendientes para esta casa
                    try {
                        const snapAgua = await getDocs(collection(db, 'fraccionamientos', fraccionamientoId, 'consumosAguaPotable'));
                        const periodosPend = [];
                        const idCasa = String(casa?.id || '').trim().toUpperCase();
                        const numeroCasa = String(casa?.numero || '').trim().toUpperCase();

                        const normalizeExact = (val) => {
                            if (!val) return '';
                            return String(val)
                                .normalize('NFD')
                                .replace(/[\u0300-\u036f]/g, '')
                                .replace(/\s+/g, '')
                                .toUpperCase();
                        };

                        const casaIdNorm = normalizeExact(idCasa);
                        const casaNumNorm = normalizeExact(numeroCasa);

                        snapAgua.forEach((docAgua) => {
                            const dataAgua = docAgua.data();
                            const desglose = dataAgua.desgloseCasas || [];
                            const itemCasa = desglose.find((c) => {
                                const cIdNorm = normalizeExact(c.casaId);
                                const cNumNorm = normalizeExact(c.casaNumero);
                                return (
                                    (cIdNorm && (cIdNorm === casaIdNorm || cIdNorm === casaNumNorm)) ||
                                    (cNumNorm && (cNumNorm === casaIdNorm || cNumNorm === casaNumNorm))
                                );
                            });

                            if (itemCasa && (itemCasa.pagado === false || itemCasa.estadoPago !== 'PAGADO')) {
                                periodosPend.push({
                                    id: docAgua.id,
                                    periodoLectura: dataAgua.periodoLectura || `Consumo ${dataAgua.mesLectura}-${dataAgua.anoLectura}`,
                                    mesLectura: dataAgua.mesLectura,
                                    anoLectura: dataAgua.anoLectura,
                                    montoPendiente: itemCasa.cargoTotal || itemCasa.montoPendiente || 0,
                                    consumoM3: itemCasa.consumoM3 || 0
                                });
                            }
                        });

                        if (casa?.id) {
                            try {
                                const multasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'multasAdministrativas');
                                const multasSnap = await getDocs(multasRef);
                                const casaIdNorm = String(casa.id || '').trim().toUpperCase();
                                const casaNumNorm = String(casa.numero || casa.numeroCasa || '').trim().toUpperCase();

                                const docsCasa = multasSnap.docs
                                    .map(d => ({ id: d.id, ...d.data() }))
                                    .filter(m => {
                                        const mCasaId = String(m.casaId || '').trim().toUpperCase();
                                        const mCasaNum = String(m.casaNumero || m.numeroCasa || '').trim().toUpperCase();
                                        return (casaIdNorm && mCasaId === casaIdNorm) || (casaNumNorm && mCasaNum === casaNumNorm);
                                    });

                                const activas = docsCasa
                                    .filter(m => !m.pagada)
                                    .map(m => ({
                                        id: m.multaId || m.id,
                                        docId: m.id,
                                        descripcion: m.multaDescripcion || m.descripcion || 'Sanción Administrativa',
                                        monto: Number(m.monto || 0),
                                        fechaFalta: m.fechaFalta,
                                        comentarios: m.comentarios
                                    }));

                                const multasActivasTotal = activas.reduce((sum, m) => sum + Number(m.monto || 0), 0);
                                setMultasPendientesMonto(multasActivasTotal);
                                setSancionesCasaList(activas);
                            } catch (errMultas) {
                                console.warn('Error buscando multasAdministrativas:', errMultas);
                            }

                            try {
                                const mesesRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas', casa.id, 'mesesPendientes');
                                const snapMeses = await getDocs(mesesRef);
                                snapMeses.forEach(docMes => {
                                    const dataMes = docMes.data() || {};
                                    const mesId = docMes.id;
                                    const est = String(dataMes.estado || '').toLowerCase();
                                    const esPagada = est === 'pagado_completo' || est === 'pagado_totalmente' || est === 'pagado';
                                    const esAguaDoc = mesId.startsWith('AGUA-') || dataMes.tipo === 'adeudo_agua';

                                    if (!esPagada && esAguaDoc) {
                                        const alreadyExists = periodosPend.some(p => p.id === mesId);
                                        if (!alreadyExists) {
                                            periodosPend.push({
                                                id: mesId,
                                                periodoLectura: dataMes.descripcion || dataMes.concepto || 'Consumo Agua Potable',
                                                mesLectura: dataMes.mes || 'Actual',
                                                anoLectura: dataMes.año || dataMes.anio || new Date().getFullYear(),
                                                montoPendiente: Number(dataMes.montoPendiente ?? dataMes.monto ?? 0),
                                                consumoM3: dataMes.consumoM3 || 0
                                            });
                                        }
                                    }
                                });
                            } catch (errM) {
                                console.warn('Error buscando mesesPendientes de agua:', errM);
                            }
                        }

                        setPeriodosAguaPendientes(periodosPend);
                    } catch (errAgua) {
                        console.error('Error cargando consumos pendientes de agua:', errAgua);
                    }
                }
            } catch (error) {
                console.error('Error cargando configuración:', error);
            } finally {
                setLoadingConfig(false);
            }
        };

        loadConfig();
    }, [fraccionamientoId, casa?.id]);

    // Extractar nombre del propietario
    const nombrePropietario =
        casa?.nombre_residente ||
        casa?.propietario ||
        casa?.nombrePropietario ||
        casa?.nombreCompleto ||
        'Propietario sin asignar';

    if (!casa) return null;

    // Función auxiliar para generar observaciones detalladas basadas en mesesPendientesData
    // Agrupa meses consecutivos completos y lista parciales individualmente
    const generarObservacionesPago = (montoNeto, prefijo = "Cuota de mantenimiento:") => {
        if (montoNeto <= 0 || cuotaMensual <= 0) return '';
        
        const mesesNombres = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
        const hoy = new Date();
        
        // Fondo total de cobertura = Saldo a Favor + Dinero Nuevo
        let fondoTotal = montoNeto + Number(casa?.saldoFavor || 0);
        let lineas = [];
        
        let mesSim = primerMesDeuda ? primerMesDeuda.mes : hoy.getMonth() + 1;
        let anioSim = primerMesDeuda ? primerMesDeuda.año : hoy.getFullYear();

        // 1. Cubrir meses de la lista de pendientes (si los hay)
        if (mesesPendientesData && mesesPendientesData.length > 0) {
            for (const mesData of mesesPendientesData) {
                if (fondoTotal <= 0) break;
                
                // Ignorar cuotas extraordinarias
                const isExtra = String(mesData.concepto || '').toLowerCase().includes('extra') || mesData.esExtraordinaria;
                if (isExtra) continue;

                // IGNORAR MESES YA PAGADOS
                const est = String(mesData.estado || '').trim().toLowerCase();
                const porEstado = ['pagado_completo', 'pagado', 'pagado_totalmente', 'pagado totalmente'].includes(est);
                
                const rawVal = mesData.montoPendiente;
                let montoMes;
                if (rawVal !== undefined && rawVal !== null && String(rawVal).trim() !== '') {
                    montoMes = Number(rawVal);
                } else {
                    const porArray = Array.isArray(mesData.pagos) && mesData.pagos.length > 0;
                    const esPagado = porEstado || porArray || !!mesData.fechaPago || mesData.esPagoAnticipo === true;
                    montoMes = esPagado ? 0 : cuotaMensual;
                }

                let considerarPagado = porEstado || montoMes <= 0;

                if (!considerarPagado && Number(casa?.saldoFavor || 0) > 0 && Number(casa?.adeudo || 0) <= 0) {
                    const aNum = Number(mesData.año || mesData.anio);
                    const mNum = parseMesNumero(mesData.mesNumero || mesData.mes);
                    if (aNum < hoy.getFullYear() || (aNum === hoy.getFullYear() && mNum <= hoy.getMonth() + 1)) {
                        considerarPagado = true;
                    }
                }

                if (considerarPagado) {
                    continue;
                }

                const mNum = parseMesNumero(mesData.mesNumero || mesData.mes);
                const aNum = Number(mesData.año || mesData.anio);

                if (fondoTotal >= montoMes) {
                    lineas.push({ mes: mNum, anio: aNum, montoAplicado: montoMes, parcial: false });
                    fondoTotal -= montoMes;
                } else {
                    lineas.push({ mes: mNum, anio: aNum, montoAplicado: fondoTotal, parcial: true });
                    fondoTotal = 0;
                }
                // Actualizar puntero de simulación para el paso 2
                mesSim = mNum + 1;
                anioSim = aNum;
                if (mesSim > 12) { mesSim = 1; anioSim++; }
            }
        }

        // 2. Cubrir meses futuros adelantados
        while (fondoTotal > 0) {
            if (fondoTotal >= cuotaMensual) {
                lineas.push({ mes: mesSim, anio: anioSim, montoAplicado: cuotaMensual, parcial: false });
                fondoTotal -= cuotaMensual;
            } else {
                lineas.push({ mes: mesSim, anio: anioSim, montoAplicado: fondoTotal, parcial: true });
                fondoTotal = 0;
            }
            mesSim++;
            if (mesSim > 12) { mesSim = 1; anioSim++; }
        }

        if (lineas.length === 0) return '';

        const formatMes = (l) => {
            const mNombre = mesesNombres[l.mes - 1];
            const mVal = Number(l.montoAplicado || 0).toFixed(2);
            const tagParcial = l.parcial ? ' parc.' : '';
            return `${mNombre} ${l.anio} ($${mVal}${tagParcial})`;
        };

        const leyenda = lineas.map(formatMes).join(', ');

        return `${prefijo} Cubre [${leyenda}]`;
    };

    // Función auxiliar integral para generar observaciones descriptivas cuando hay desglose multi-partida
    // Respeta meses específicos seleccionados, cuotas extraordinarias, agua, recargos y modo automático (FIFO)
    const generarLeyendaCoberturaPago = () => {
        const mesesNombres = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
        const bloques = [];

        // 1. Partidas de Mantenimiento / Adeudos
        const partidasMantto = pagos.filter(p => {
            const esMant = esConceptoMantenimiento(p.concepto);
            return esMant && Number(p.monto || 0) > 0;
        });

        const itemsMantto = [];
        const idsMesesCubiertos = new Set();
        let totalManttoAuto = 0;

        for (const p of partidasMantto) {
            const tieneMesEspecifico = p.mesSeleccionadoId || (p.mesSeleccionadoTexto && p.mesSeleccionadoTexto !== 'Automático (FIFO)');
            if (tieneMesEspecifico) {
                const mesData = mesesPendientesData.find(m => String(m.id) === String(p.mesSeleccionadoId));
                const montoPendiente = Number(mesData?.montoPendiente ?? mesData?.monto ?? p.mesSeleccionadoMonto ?? cuotaMensual);
                const montoP = Number(p.monto || 0);
                const esParcial = montoPendiente > 0 && Math.round(montoP * 100) < Math.round(montoPendiente * 100);
                const desc = p.mesSeleccionadoTexto || (mesData ? `${mesesNombres[parseMesNumero(mesData.mesNumero || mesData.mes) - 1]} ${mesData.año || mesData.anio}` : 'Mes seleccionado');
                itemsMantto.push(`${desc} ($${montoP.toFixed(2)}${esParcial ? ' parc.' : ''})`);
                if (p.mesSeleccionadoId) {
                    idsMesesCubiertos.add(String(p.mesSeleccionadoId));
                }
            } else {
                totalManttoAuto += Number(p.monto || 0);
            }
        }

        // Si hay excedente global capturado sobre el desglose, se suma a mantenimiento automático
        const globalNum = parseFloat(montoTotalCap || 0);
        const capturadoVal = globalNum > 0 ? globalNum : totalMonto;
        const difExcedente = Math.round((capturadoVal - totalMonto) * 100) / 100;
        if (difExcedente > 0) {
            totalManttoAuto += difExcedente;
        }

        // Si hay monto de mantenimiento automático, procesar con FIFO omitiendo los meses ya seleccionados en partidas específicas
        if (totalManttoAuto > 0 && cuotaMensual > 0) {
            let fondoRestante = totalManttoAuto;
            const hoy = new Date();
            let mesSim = primerMesDeuda ? primerMesDeuda.mes : hoy.getMonth() + 1;
            let anioSim = primerMesDeuda ? primerMesDeuda.año : hoy.getFullYear();

            if (mesesPendientesData && mesesPendientesData.length > 0) {
                for (const mesData of mesesPendientesData) {
                    if (fondoRestante <= 0) break;
                    if (idsMesesCubiertos.has(String(mesData.id))) continue;

                    const isExtra = String(mesData.concepto || '').toLowerCase().includes('extra') || mesData.esExtraordinaria;
                    if (isExtra) continue;

                    const est = String(mesData.estado || '').trim().toLowerCase();
                    const porEstado = ['pagado_completo', 'pagado', 'pagado_totalmente', 'pagado totalmente'].includes(est);
                    const rawVal = mesData.montoPendiente;
                    const montoMes = (rawVal !== undefined && rawVal !== null && String(rawVal).trim() !== '')
                        ? Number(rawVal)
                        : cuotaMensual;

                    if (porEstado || montoMes <= 0) continue;

                    const mNum = parseMesNumero(mesData.mesNumero || mesData.mes);
                    const aNum = Number(mesData.año || mesData.anio);
                    const mNombre = mesesNombres[mNum - 1] || mesData.mes;

                    if (fondoRestante >= montoMes) {
                        itemsMantto.push(`${mNombre} ${aNum} ($${montoMes.toFixed(2)})`);
                        fondoRestante -= montoMes;
                    } else {
                        itemsMantto.push(`${mNombre} ${aNum} ($${fondoRestante.toFixed(2)} parc.)`);
                        fondoRestante = 0;
                    }

                    mesSim = mNum + 1;
                    anioSim = aNum;
                    if (mesSim > 12) { mesSim = 1; anioSim++; }
                }
            }

            while (fondoRestante > 0) {
                const mNombre = mesesNombres[mesSim - 1];
                if (fondoRestante >= cuotaMensual) {
                    itemsMantto.push(`${mNombre} ${anioSim} ($${cuotaMensual.toFixed(2)})`);
                    fondoRestante -= cuotaMensual;
                } else {
                    itemsMantto.push(`${mNombre} ${anioSim} ($${fondoRestante.toFixed(2)} parc.)`);
                    fondoRestante = 0;
                }
                mesSim++;
                if (mesSim > 12) { mesSim = 1; anioSim++; }
            }
        }

        if (itemsMantto.length > 0) {
            bloques.push(`Cuota de mantenimiento: Cubre [${itemsMantto.join(', ')}]`);
        }

        // 2. Partidas de Cuotas Extraordinarias
        const partidasExtras = pagos.filter(p => {
            const esExtra = esConceptoCuotaExtra(p.concepto, p.cuotaExtraId);
            return esExtra && Number(p.monto || 0) > 0;
        });

        if (partidasExtras.length > 0) {
            const itemsExtras = partidasExtras.map(p => {
                const cuotaObj = cuotasExtras.find(c => String(c.id) === String(p.cuotaExtraId));
                const nombreCuota = cuotaObj?.descripcion || p.cuotaExtraDesc || 'Cuota Extraordinaria';
                const montoPendienteCuota = Number(cuotaObj?.monto ?? 0);
                const montoP = Number(p.monto || 0);
                const esParcial = montoPendienteCuota > 0 && Math.round(montoP * 100) < Math.round(montoPendienteCuota * 100);
                return `${nombreCuota} ($${montoP.toFixed(2)}${esParcial ? ' parc.' : ''})`;
            });
            bloques.push(`Cuota Extraordinaria: ${itemsExtras.join(', ')}`);
        }

        // 3. Partidas de Agua Potable
        const partidasAgua = pagos.filter(p => {
            const esAgua = esConceptoAgua(p.concepto, p.periodoAguaId);
            return esAgua && Number(p.monto || 0) > 0;
        });

        if (partidasAgua.length > 0) {
            const itemsAgua = partidasAgua.map(p => {
                const pAgua = periodosAguaPendientes.find(a => String(a.id) === String(p.periodoAguaId));
                const nombrePeriodo = pAgua?.periodoLectura || 'Consumo Agua Potable';
                return `${nombrePeriodo} ($${Number(p.monto || 0).toFixed(2)})`;
            });
            bloques.push(`Agua Potable: ${itemsAgua.join(', ')}`);
        }

        // 4. Partidas de Recargos o Intereses
        const partidasRecargos = pagos.filter(p => {
            return esConceptoRecargos(p.concepto) && Number(p.monto || 0) > 0;
        });

        if (partidasRecargos.length > 0) {
            const montoRecargos = partidasRecargos.reduce((sum, p) => sum + Number(p.monto || 0), 0);
            bloques.push(`Recargos e intereses ($${montoRecargos.toFixed(2)})`);
        }

        // 5. Partidas de Multas Administrativas
        const partidasMultas = pagos.filter(p => {
            return esConceptoMulta(p.concepto, p.multaAdminId) && Number(p.monto || 0) > 0;
        });

        if (partidasMultas.length > 0) {
            const itemsMultas = partidasMultas.map(p => {
                return `${p.multaAdminDesc || 'Multa Administrativa'} ($${Number(p.monto || 0).toFixed(2)})`;
            });
            bloques.push(`Multas: ${itemsMultas.join(', ')}`);
        }

        // 6. Otros Conceptos de Tesorería (Categoría B)
        const partidasOtros = pagos.filter(p => {
            if (Number(p.monto || 0) <= 0) return false;
            return !esPartidaContableResidente(p);
        });

        if (partidasOtros.length > 0) {
            const itemsOtros = partidasOtros.map(p => `${p.concepto || 'Otro concepto'} ($${Number(p.monto || 0).toFixed(2)})`);
            bloques.push(`Otros conceptos: ${itemsOtros.join(', ')}`);
        }

        return bloques.join('; ');
    };

    // Función de cascada automática de prioridades para desglose multi-concepto:
    // P1: Cuota de Mantenimiento / Adeudos Anteriores
    // P2: Consumo de Agua Potable
    // P3: Cuotas Extraordinarias
    // Remanente para morosos (adeudoTotal > 0): Destina obligatoriamente a Adeudos Anteriores / Recargos (NUNCA Saldo a Favor)
    // Remanente para al corriente (adeudoTotal == 0): Destina a Saldo a Favor
    const aplicarCascadaPrioridades = (globalNum, listPagos) => {
        if (!listPagos || listPagos.length === 0) return listPagos;

        let pool = Math.max(0, parseFloat(globalNum || 0));

        // Mantenimiento / Adeudo
        const reqMantenimiento = cuotaMensual > 0 ? cuotaMensual : (adeudoTotal > 0 ? adeudoTotal : 0);
        // Agua
        const primerAgua = periodosAguaPendientes && periodosAguaPendientes.length > 0 ? periodosAguaPendientes[0] : null;
        const reqAgua = primerAgua ? Number(primerAgua.montoPendiente || primerAgua.montoPagar || primerAgua.monto || 0) : 0;
        // Cuota Extra
        const primeraExtra = cuotasExtras && cuotasExtras.length > 0 ? cuotasExtras[0] : null;
        const reqExtra = primeraExtra ? Number(primeraExtra.monto || 0) : 0;

        const resultado = listPagos.map((p, idx) => {
            const concLower = String(p.concepto || '').toLowerCase();
            const esMant = concLower.includes('mantenimiento') || concLower.includes('mantto') || concLower.includes('adeudo') || (!p.concepto && idx === 0);
            const esAgua = concLower.includes('agua') || concLower.includes('cea');
            const esExtra = concLower.includes('extraordinaria') || concLower.includes('cuota extra') || p.cuotaExtraId;

            if (esMant) {
                const targetReq = reqMantenimiento > 0 ? reqMantenimiento : pool;
                const asignado = Math.min(pool, targetReq);
                pool = Math.max(0, Math.round((pool - asignado) * 100) / 100);
                return {
                    ...p,
                    concepto: p.concepto || (adeudoTotal > 0 ? 'Adeudos Anteriores' : 'Cuota de Mantenimiento'),
                    monto: Math.round(asignado * 100) / 100
                };
            }
            if (esAgua) {
                const targetReq = reqAgua > 0 ? reqAgua : pool;
                const asignado = Math.min(pool, targetReq);
                pool = Math.max(0, Math.round((pool - asignado) * 100) / 100);
                return {
                    ...p,
                    monto: Math.round(asignado * 100) / 100,
                    ...(primerAgua && !p.periodoAguaId ? { periodoAguaId: primerAgua.id } : {})
                };
            }
            if (esExtra) {
                const targetReq = reqExtra > 0 ? reqExtra : pool;
                const asignado = Math.min(pool, targetReq);
                pool = Math.max(0, Math.round((pool - asignado) * 100) / 100);
                return {
                    ...p,
                    monto: Math.round(asignado * 100) / 100,
                    ...(primeraExtra && !p.cuotaExtraId ? { cuotaExtraId: primeraExtra.id, cuotaExtraDesc: primeraExtra.descripcion } : {})
                };
            }

            const asignado = Math.min(pool, pool);
            pool = Math.max(0, Math.round((pool - asignado) * 100) / 100);
            return {
                ...p,
                monto: Math.round(asignado * 100) / 100
            };
        });

        // Si la casa es morosa (adeudoTotal > 0) y aún queda pool > 0 tras recorrer las partidas activas
        // Se asigna obligatoriamente a Adeudos Anteriores para liquidar deuda histórica
        if (pool > 0 && adeudoTotal > 0) {
            const idxAdeudo = resultado.findIndex(p => {
                const c = String(p.concepto || '').toLowerCase();
                return c.includes('adeudo') || c.includes('mantenimiento') || c.includes('mantto') || c.includes('recargo');
            });
            if (idxAdeudo >= 0) {
                resultado[idxAdeudo].monto = Math.round((resultado[idxAdeudo].monto + pool) * 100) / 100;
            }
        }

        return resultado;
    };

    // Ajustar excedente y crear partida de excedente si corresponde en cascada inteligente
    const actualizarPartidasConExcedente = (listaPartidas, totalRecibido, idEditado = null) => {
        if (!listaPartidas || listaPartidas.length === 0) return listaPartidas;

        const adeudoAguaActual = Number(casa?.adeudoAgua || 0);
        const cuotasExtrasActual = Number(casa?.cuotasExtras || 0);
        const sancionesAdminActual = Number(casa?.sancionesAdmin || 0);
        const totalAdeudoGlobal = Number(casa?.adeudo || 0);

        // Usar el cálculo estimado de capital e intereses para asegurar que totalManttoDeuda refleje los meses pendientes reales
        const realCapital = estimateCapital();
        const realIntereses = estimateIntereses(realCapital);
        const totalManttoDeuda = realCapital + realIntereses;
        const esResidenteMoroso = totalAdeudoGlobal > 0 || totalManttoDeuda > 0;

        let resultado = [...listaPartidas];

        // Verificar y ajustar deudas especificas por partida
        for (let i = 0; i < resultado.length; i++) {
            const p = resultado[i];
            const conceptoLower = String(p.concepto || '').toLowerCase();
            const montoVal = parseFloat(p.monto || 0);

            if (montoVal <= 0 || !p.concepto) continue;

            const esMantto = conceptoLower.includes('cuota') && conceptoLower.includes('mantenimiento') && !conceptoLower.includes('extraordinaria');
            const esExtra = conceptoLower.includes('extraordinaria');
            const esMulta = conceptoLower.includes('multa') || conceptoLower.includes('sancion');
            const esAgua = conceptoLower.includes('agua') || conceptoLower.includes('cea');

            let limiteMaximo = null;

            if (esMantto) {
                // Si el residente es moroso (debe mantenimiento u otros conceptos), limitamos Mantenimiento a (Intereses + Capital)
                if (esResidenteMoroso && totalManttoDeuda > 0 && montoVal > totalManttoDeuda) {
                    limiteMaximo = totalManttoDeuda;
                }
            } else if (esExtra && p.cuotaExtraId) {
                const selectedCuota = cuotasExtras.find(c => String(c.id) === String(p.cuotaExtraId));
                if (selectedCuota) {
                    const cleanName = String(selectedCuota.descripcion || '').replace(/\s+/g, '').substring(0, 15);
                    const mesActualStr = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
                    const targetId = `EXT-${cleanName}-${mesActualStr}`;
                    const docReal = rawMesesData.find(m => m.id === selectedCuota.id || m.mesId === selectedCuota.id) || 
                                    rawMesesData.find(m => (m.id === targetId || m.mesId === targetId || (m.id && m.id.startsWith(`EXT-${cleanName}-`))));
                    const maxCuota = docReal && docReal.montoPendiente !== undefined ? Number(docReal.montoPendiente) : Number(selectedCuota.monto || 0);
                    if (montoVal > maxCuota) limiteMaximo = maxCuota;
                }
            } else if (esMulta && p.multaAdminId) {
                const selectedMulta = sancionesCasaList.find(m => String(m.id) === String(p.multaAdminId) || String(m.docId) === String(p.multaAdminId));
                if (selectedMulta) {
                    const maxMulta = Number(selectedMulta.monto || 0);
                    if (montoVal > maxMulta) limiteMaximo = maxMulta;
                }
            } else if (esAgua && p.periodoAguaId) {
                const selectedPeriodo = periodosAguaPendientes.find(pAgua => String(pAgua.id) === String(p.periodoAguaId));
                if (selectedPeriodo) {
                    const maxAgua = Number(selectedPeriodo.montoPendiente || 0);
                    if (montoVal > maxAgua) limiteMaximo = maxAgua;
                }
            }

            if (limiteMaximo !== null && montoVal > limiteMaximo) {
                const excedente = Math.round((montoVal - limiteMaximo) * 100) / 100;
                resultado[i] = {
                    ...p,
                    monto: limiteMaximo
                };

                const siguienteIndice = i + 1;
                if (siguienteIndice < resultado.length) {
                    resultado[siguienteIndice] = {
                        ...resultado[siguienteIndice],
                        monto: Math.round(((resultado[siguienteIndice].monto || 0) + excedente) * 100) / 100
                    };
                } else {
                    const conceptosYaUsados = resultado.map(item => String(item.concepto || '').toLowerCase()).filter(Boolean);

                    let conceptoSugerido = '';
                    if (adeudoAguaActual > 0 && !conceptosYaUsados.some(c => c.includes('agua'))) {
                        conceptoSugerido = 'Consumo de Agua Potable';
                    } else if (cuotasExtrasActual > 0 && !conceptosYaUsados.some(c => c.includes('extraordinaria'))) {
                        conceptoSugerido = 'Cuota Extraordinaria';
                    } else if (sancionesAdminActual > 0 && !conceptosYaUsados.some(c => c.includes('multa') || c.includes('sancion'))) {
                        conceptoSugerido = 'Sanciones Administrativas';
                    } else if (totalManttoDeuda > 0 && !conceptosYaUsados.some(c => c.includes('mantenimiento'))) {
                        conceptoSugerido = 'Cuota de Mantenimiento';
                    }

                    const nextId = resultado.length > 0 ? Math.max(...resultado.map(item => item.id)) + 1 : 1;
                    resultado.push({
                        id: nextId,
                        monto: excedente,
                        concepto: conceptoSugerido,
                        descuento: 0,
                        fechaPago: fechaPagoGeneral || new Date().toISOString().split('T')[0],
                        metodoPago: metodoPagoGeneral || 'Transferencia Bancaria',
                        referencia: referenciaGeneral || '',
                        observaciones: '',
                        observacionesAuto: `Excedente asignado para cubrir otros adeudos pendientes.`,
                        comprobante: comprobanteGeneral || null,
                        multaAdminId: null,
                        multaAdminDesc: '',
                        cuotaExtraId: null,
                        cuotaExtraDesc: ''
                    });
                }
            }
        }

        // Manejo de monto global cap si aplica
        const total = parseFloat(totalRecibido || 0);
        if (total > 0) {
            let partidasDefinidas = [];
            let partidaExcedente = null;

            for (let i = 0; i < resultado.length; i++) {
                const p = resultado[i];
                if (!p.concepto && i === resultado.length - 1) {
                    partidaExcedente = p;
                } else {
                    partidasDefinidas.push(p);
                }
            }

            const sumaDefinidas = partidasDefinidas.reduce((sum, p) => sum + parseFloat(p.monto || 0), 0);
            const excedenteGlobal = Math.round((total - sumaDefinidas) * 100) / 100;

            if (excedenteGlobal > 0) {
                if (partidaExcedente) {
                    partidaExcedente.monto = excedenteGlobal;
                    return [...partidasDefinidas, partidaExcedente];
                } else {
                    const nextId = resultado.length > 0 ? Math.max(...resultado.map(p => p.id)) + 1 : 1;
                    const nuevaPartida = {
                        id: nextId,
                        monto: excedenteGlobal,
                        concepto: '',
                        descuento: 0,
                        fechaPago: fechaPagoGeneral || new Date().toISOString().split('T')[0],
                        metodoPago: metodoPagoGeneral || 'Transferencia Bancaria',
                        referencia: referenciaGeneral || '',
                        observaciones: '',
                        observacionesAuto: '',
                        comprobante: comprobanteGeneral || null,
                        multaAdminId: null,
                        multaAdminDesc: '',
                        cuotaExtraId: null,
                        cuotaExtraDesc: ''
                    };
                    return [...partidasDefinidas, nuevaPartida];
                }
            } else {
                return partidasDefinidas;
            }
        }

        return resultado;
    };

    // Agregar nueva partida al desglose por conceptos
    const handleAddPago = () => {
        const nextId = pagos.length > 0 ? Math.max(...pagos.map(p => p.id)) + 1 : 1;
        const sumaActual = pagos.reduce((sum, p) => sum + parseFloat(p.monto || 0), 0);
        const globalNum = parseFloat(montoTotalCap || 0);
        const restante = Math.max(0, globalNum - sumaActual);

        setPagos([...pagos, {
            id: nextId,
            monto: restante > 0 ? Math.round(restante * 100) / 100 : 0,
            concepto: '',
            descuento: 0,
            fechaPago: fechaPagoGeneral || new Date().toISOString().split('T')[0],
            metodoPago: metodoPagoGeneral || 'Transferencia Bancaria',
            referencia: referenciaGeneral || '',
            observaciones: '',
            observacionesAuto: '',
            comprobante: comprobanteGeneral || null,
            multaAdminId: null,
            multaAdminDesc: '',
            cuotaExtraId: null,
            cuotaExtraDesc: '',
            mesSeleccionadoId: null,
            mesSeleccionadoTexto: '',
            mesSeleccionadoMonto: 0
        }]);
    };

    // Eliminar partida del desglose por conceptos sin alterar los montos ya definidos en las demás partidas
    const handleRemovePago = (id) => {
        if (pagos.length > 1) {
            const remaining = pagos.filter(p => p.id !== id);
            setPagos(remaining);
        }
    };

    // Seleccionar mes específico o automático para una partida de mantenimiento
    const handleSeleccionarMesParaPartida = (mesObj) => {
        if (!partidaParaSeleccionarMes) {
            setModalSelectorMesOpen(false);
            return;
        }

        const targetId = partidaParaSeleccionarMes;
        if (!mesObj) {
            // Modo automático FIFO
            handleUpdatePago(targetId, {
                mesSeleccionadoId: null,
                mesSeleccionadoTexto: 'Automático (FIFO)',
                mesSeleccionadoMonto: cuotaMensual > 0 ? cuotaMensual : 0,
                monto: cuotaMensual > 0 ? cuotaMensual : 0,
                observacionesAuto: generarObservacionesPago(cuotaMensual, 'Cuota de mantenimiento:')
            });
        } else {
            // Mes específico seleccionado
            const montoMes = Number(mesObj.montoPendiente ?? mesObj.monto ?? cuotaMensual);
            handleUpdatePago(targetId, {
                mesSeleccionadoId: mesObj.id,
                mesSeleccionadoTexto: `${mesObj.mesNombre} ${mesObj.anio}`,
                mesSeleccionadoMonto: montoMes,
                monto: montoMes,
                observacionesAuto: `Cuota de mantenimiento: ${mesObj.mesNombre} ${mesObj.anio} ($${montoMes.toFixed(2)})`
            });
        }

        setModalSelectorMesOpen(false);
        setPartidaParaSeleccionarMes(null);
    };

    // Sugerencia rápida para liquidar adeudo de intereses moratorios
    const handleAgregarPartidaIntereses = () => {
        if (adeudoInteresesCasa <= 0) return;

        // Si ya existe una partida con "Recargos o intereses", actualizarla
        const indexExistente = pagos.findIndex(p => {
            const cLower = String(p.concepto || '').toLowerCase();
            return cLower.includes('recargo') || cLower.includes('inter');
        });

        if (indexExistente >= 0) {
            handleUpdatePago(pagos[indexExistente].id, {
                monto: adeudoInteresesCasa
            });
            Swal.fire({
                icon: 'info',
                title: 'Partida ya existente',
                text: `La partida #${indexExistente + 1} ya tiene asignado el concepto de intereses por $${adeudoInteresesCasa.toFixed(2)}.`,
                timer: 2000,
                showConfirmButton: false
            });
            return;
        }

        // Si la primera partida está vacía (sin concepto ni monto), reusarla
        if (pagos.length === 1 && !pagos[0].concepto && Number(pagos[0].monto || 0) === 0) {
            handleUpdatePago(pagos[0].id, {
                concepto: 'Recargos o intereses',
                monto: adeudoInteresesCasa
            });
            return;
        }

        // De lo contrario, agregar una nueva partida
        const nextId = pagos.length > 0 ? Math.max(...pagos.map(p => p.id)) + 1 : 1;
        const nuevaPartida = {
            id: nextId,
            monto: adeudoInteresesCasa,
            concepto: 'Recargos o intereses',
            descuento: 0,
            fechaPago: fechaPagoGeneral || new Date().toISOString().split('T')[0],
            metodoPago: metodoPagoGeneral || 'Transferencia Bancaria',
            referencia: referenciaGeneral || '',
            observaciones: '',
            observacionesAuto: '',
            comprobante: comprobanteGeneral || null,
            multaAdminId: null,
            multaAdminDesc: '',
            cuotaExtraId: null,
            cuotaExtraDesc: '',
            mesSeleccionadoId: null,
            mesSeleccionadoTexto: '',
            mesSeleccionadoMonto: 0
        };
        const nextPagos = [...pagos, nuevaPartida];
        const globalNumCap = parseFloat(montoTotalCap || 0);
        setPagos(actualizarPartidasConExcedente(nextPagos, globalNumCap));
    };

    // Actualizar pago
    const handleUpdatePago = (id, field, value) => {
        const updatedPagos = pagos.map(p => {
            if (p.id === id) {
                const updatedPago = typeof field === 'object'
                    ? { ...p, ...field }
                    : { ...p, [field]: value };

                // Actualización de pago




                // Función auxiliar: generar desglose mensual marcando filas parciales
                const generarDesgloseMensualLocal = (montoNeto) => {
                    if (montoNeto <= 0 || cuotaMensual <= 0) return null;
                    const mesesDelAnio = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
                    let restante = montoNeto + Number(casa?.saldoFavor || 0);
                    const hoy = new Date();
                    let indiceMes = primerMesDeuda ? primerMesDeuda.mes - 1 : hoy.getMonth();
                    let anioSim = primerMesDeuda ? primerMesDeuda.año : hoy.getFullYear();
                    const desglose = [];
                    if (mesesPendientesData && mesesPendientesData.length > 0) {
                        for (const mesData of mesesPendientesData) {
                            if (restante <= 0) break;

                            // Ignorar cuotas extraordinarias
                            const isExtra = String(mesData.concepto || '').toLowerCase().includes('extra') || mesData.esExtraordinaria;
                            if (isExtra) continue;

                            // IGNORAR MESES YA PAGADOS
                            const est = String(mesData.estado || '').trim().toLowerCase();
                            const porEstado = ['pagado_completo', 'pagado', 'pagado_totalmente', 'pagado totalmente'].includes(est);

                            const rawVal = mesData.montoPendiente;
                            let pendienteMes;
                            if (rawVal !== undefined && rawVal !== null && String(rawVal).trim() !== '') {
                                pendienteMes = Number(rawVal);
                            } else {
                                const porArray = Array.isArray(mesData.pagos) && mesData.pagos.length > 0;
                                const esPagado = porEstado || porArray || !!mesData.fechaPago || mesData.esPagoAnticipo === true;
                                pendienteMes = esPagado ? 0 : cuotaMensual;
                            }

                            let considerarPagado = porEstado || pendienteMes <= 0;

                            if (!considerarPagado && Number(casa?.saldoFavor || 0) > 0 && Number(casa?.adeudo || 0) <= 0) {
                                const aNum = Number(mesData.año || mesData.anio);
                                const mNum = parseMesNumero(mesData.mesNumero || mesData.mes);
                                if (aNum < hoy.getFullYear() || (aNum === hoy.getFullYear() && mNum <= hoy.getMonth() + 1)) {
                                    considerarPagado = true;
                                }
                            }

                            if (considerarPagado) {
                                continue;
                            }

                            const idx = (parseMesNumero(mesData.mesNumero || mesData.mes) - 1) % 12;
                            const anioStr = Number(mesData.año || mesData.anio);
                            const importeCubierto = Math.min(restante, pendienteMes);
                            const esParcial = importeCubierto < pendienteMes;
                            desglose.push({ mes: mesesDelAnio[idx], mesNumero: idx + 1, año: anioStr, importe: importeCubierto, esParcial });
                            restante -= importeCubierto;
                            indiceMes = (idx + 1) % 12;
                            anioSim = idx === 11 ? anioStr + 1 : anioStr;
                        }
                    }
                    while (restante > 0) {
                        const importeCubierto = Math.min(restante, cuotaMensual);
                        const esParcial = importeCubierto < cuotaMensual;
                        desglose.push({ mes: mesesDelAnio[indiceMes], mesNumero: indiceMes + 1, año: anioSim, importe: importeCubierto, esParcial });
                        restante -= importeCubierto;
                        indiceMes++;
                        if (indiceMes > 11) { indiceMes = 0; anioSim++; }
                    }
                    return desglose.length > 0 ? desglose : null;
                };

                // Si cambió el concepto, validar y aplicar lógica especial
                if (field === 'concepto') {
                    const conceptoLower = value.toLowerCase();
                    const esCuotaMantenimiento = conceptoLower.includes('cuota') && conceptoLower.includes('mantenimiento') && !conceptoLower.includes('extraordinaria');
                    const esAdeudoAnterior = conceptoLower.includes('adeudo') && (conceptoLower.includes('anterior') || conceptoLower.includes('pendiente'));
                    const esRecargos = conceptoLower.includes('recargo') || conceptoLower.includes('inter');

                    // Control de selección de mes específico
                    if (!esCuotaMantenimiento && !esAdeudoAnterior) {
                        updatedPago.mesSeleccionadoId = null;
                        updatedPago.mesSeleccionadoTexto = '';
                        updatedPago.mesSeleccionadoMonto = 0;
                    } else if (moduloSeleccionMesesActivo && mesesPendientesData && mesesPendientesData.length > 0) {
                        setPartidaParaSeleccionarMes(id);
                        setModalSelectorMesOpen(true);
                    }

                    // AUTO-COMPLETAR MONTO SI EL CONCEPTO TIENE PRECIO FIJO/CUOTA
                    const precioFijo = receiptConfig.conceptosPrices?.[value];
                    let tienePrecioFijo = false;
                    let nuevoMonto = 0;

                    if (precioFijo && precioFijo > 0) {
                        nuevoMonto = precioFijo;
                        tienePrecioFijo = true;
                    } else if (conceptoLower.includes('pago') && conceptoLower.includes('anual') && conceptoLower.includes('cuota') && conceptoLower.includes('mantto')) {
                        if (cuotaMensual > 0) nuevoMonto = cuotaMensual * 12;
                        tienePrecioFijo = true;
                    } else if (esCuotaMantenimiento) {
                        if (cuotaMensual > 0) nuevoMonto = cuotaMensual;
                        tienePrecioFijo = true;
                    } else if (conceptoLower.includes('agua') || conceptoLower.includes('cea')) {
                        if (periodosAguaPendientes && periodosAguaPendientes.length > 0) {
                            const primerPeriodo = periodosAguaPendientes[0];
                            nuevoMonto = Number(primerPeriodo.montoPendiente || 0);
                            updatedPago.periodoAguaId = primerPeriodo.id;
                            updatedPago.saldoFavorConsumido = 0;
                            updatedPago.observacionesAuto = `Pago de consumo de agua: ${primerPeriodo.periodoLectura} (${primerPeriodo.consumoM3} m³)`;
                            tienePrecioFijo = true;
                        }
                    } else if (esConceptoSancion(value)) {
                        const sancionPendiente = sancionesCasaList.find(s => !pagos.some(other => other.id !== id && String(other.multaAdminId) === String(s.id))) || sancionesCasaList[0];
                        if (sancionPendiente) {
                            nuevoMonto = Number(sancionPendiente.monto || 0);
                            updatedPago.multaAdminId = sancionPendiente.id;
                            updatedPago.multaAdminDesc = sancionPendiente.descripcion;
                            updatedPago.observacionesAuto = `Pago de Sanción Administrativa: ${sancionPendiente.descripcion} ($${nuevoMonto.toFixed(2)})`;
                            tienePrecioFijo = true;
                        } else {
                            nuevoMonto = 0;
                            updatedPago.multaAdminId = '';
                            updatedPago.multaAdminDesc = '';
                            updatedPago.observacionesAuto = '';
                            tienePrecioFijo = true;
                        }
                    }

                    if (tienePrecioFijo) {
                        updatedPago.monto = nuevoMonto;
                    }

                    updatedPago.observaciones = '';
                    updatedPago.descuento = 0;

                    // Pago Anual
                    if (conceptoLower.includes('pago') && conceptoLower.includes('anual') && conceptoLower.includes('cuota') && conceptoLower.includes('mantto')) {
                        updatedPago.observacionesAuto = generarObservacionesPago(updatedPago.monto, 'Pago anual:');
                    }
                    // Descuentos Pronto Pago
                    else if (conceptoLower.includes('descuento') || conceptoLower.includes('pronto pago')) {
                        if (adeudoTotal > 0) {
                            setErrorMessage('⚠️ Este concepto solo aplica para residentes AL CORRIENTE (sin deuda).');
                            return p;
                        }
                        if (descuentoEnabled && descuentoConfiguracion > 0) {
                            updatedPago.descuento = descuentoConfiguracion;
                            if (p.id === 1 && cuotaMensual > 0) updatedPago.monto = cuotaMensual;
                            const mNeto = updatedPago.monto * (1 - (updatedPago.descuento / 100));
                            updatedPago.observacionesAuto = generarObservacionesPago(mNeto, `Descuento pronto pago ${descuentoConfiguracion}%:`);
                        } else {
                            if (p.id === 1 && cuotaMensual > 0) updatedPago.monto = cuotaMensual;
                            updatedPago.observacionesAuto = '';
                        }
                    }
                    // Cuota de Mantenimiento
                    else if (esCuotaMantenimiento) {
                        updatedPago.observacionesAuto = generarObservacionesPago(updatedPago.monto, 'Cuota de mantenimiento:');
                    }
                    // Adeudos Anteriores / Pendientes
                    else if (esAdeudoAnterior) {
                        if (p.id === 1 && !precioFijo && adeudoTotalActual > 0) {
                            updatedPago.monto = adeudoTotalActual;
                        }
                        const mAdeudo = updatedPago.monto || 0;
                        updatedPago.observacionesAuto = mAdeudo > 0 ? generarObservacionesPago(mAdeudo, 'Abono a adeudo:') : '';
                    }
                    // Recargos e Intereses
                    else if (esRecargos) {
                        // Leer directamente el campo de Firestore para validar
                        const interesesReales = casa && Object.prototype.hasOwnProperty.call(casa, 'adeudoIntereses')
                            ? Number(casa.adeudoIntereses || 0)
                            : adeudoInteresesActual;
                        if (interesesReales <= 0) {
                            setErrorMessage('⚠️ Esta casa no tiene intereses o recargos pendientes. Si hay adeudo de capital, usa el concepto "Adeudos Anteriores".');
                            return p; // bloquear sin modificar el pago
                        }
                        if (p.id === 1 && !precioFijo) {
                            const totalIntereses = adeudoInteresesActual + adeudoCapitalActual;
                            if (totalIntereses > 0) updatedPago.monto = Math.round(totalIntereses * 100) / 100;
                        }
                        const montoRec = updatedPago.monto || 0;
                        if (montoRec > 0) {
                            const previewRec = computePaymentPreview(montoRec, adeudoCapitalActual, adeudoInteresesActual, Number(casa?.saldoFavor || 0));
                            const partes = [];
                            if (previewRec.aplicadoIntereses > 0) partes.push(`$${previewRec.aplicadoIntereses.toFixed(2)} aplicado a intereses`);
                            if (previewRec.aplicadoCapital > 0) partes.push(`$${previewRec.aplicadoCapital.toFixed(2)} aplicado a capital`);
                            if (previewRec.saldoFavorGenerado > 0) partes.push(`$${previewRec.saldoFavorGenerado.toFixed(2)} a saldo a favor`);
                            if (previewRec.adeudoNuevo > 0) partes.push(`adeudo restante $${previewRec.adeudoNuevo.toFixed(2)}`);
                            updatedPago.observacionesAuto = partes.length > 0 ? `Recargos e intereses: ${partes.join(', ')}` : '';
                        }
                    }
                    // Cuotas Extraordinarias
                    else if (conceptoLower.includes('extraordinaria')) {
                        const mExtraVal = parseFloat(updatedPago.monto || 0);
                        if (mExtraVal > 0) {
                            updatedPago.observacionesAuto = `Pago de Cuota Extraordinaria: ${p.cuotaExtraDesc || ''}`;
                        }
                    }
                    // Sanciones Administrativas
                    else if (esConceptoSancion(conceptoLower)) {
                        const mSancionVal = parseFloat(updatedPago.monto || 0);
                        if (mSancionVal > 0 && updatedPago.multaAdminDesc) {
                            updatedPago.observacionesAuto = `Pago de Sanción Administrativa: ${updatedPago.multaAdminDesc} ($${mSancionVal.toFixed(2)})`;
                        }
                    }
                    // Conceptos Misceláneos (Categoría B)
                    else if (!esPartidaContableResidente(updatedPago)) {
                        updatedPago.observacionesAuto = 'Ingreso de tesorería (No afecta adeudo ni genera saldo a favor)';
                    }
                }

                // Si cambió el monto, recalcular observaciones
                if (field === 'monto') {
                    const conceptoLower = String(p.concepto || '').toLowerCase();
                    const esCuota = conceptoLower.includes('cuota') && conceptoLower.includes('mantenimiento') && !conceptoLower.includes('extraordinaria');
                    const esDesc = conceptoLower.includes('descuento') || conceptoLower.includes('pronto pago');
                    const esAnual = conceptoLower.includes('pago') && conceptoLower.includes('anual');
                    const esAdeudo = conceptoLower.includes('adeudo') && (conceptoLower.includes('anterior') || conceptoLower.includes('pendiente'));
                    const esRecargo = conceptoLower.includes('recargo') || conceptoLower.includes('inter');
                    const esExtra = conceptoLower.includes('extraordinaria');

                    // Para todos los conceptos: recalcular observacionesAuto (recuadro azul), nunca observaciones
                    if (esAnual) {
                        const mNeto = parseFloat(value || 0) * (1 - ((p.descuento || 0) / 100));
                        updatedPago.observacionesAuto = generarObservacionesPago(mNeto, 'Pago anual:');
                    } else if (esCuota) {
                        const mNeto = parseFloat(value || 0) * (1 - ((p.descuento || 0) / 100));
                        const tieneMes = p.mesSeleccionadoId || (p.mesSeleccionadoTexto && p.mesSeleccionadoTexto !== 'Automático (FIFO)');
                        if (tieneMes) {
                            const montoBase = Number(p.mesSeleccionadoMonto || cuotaMensual);
                            const esAbono = mNeto > 0 && Math.round(mNeto * 100) < Math.round(montoBase * 100);
                            updatedPago.observacionesAuto = `Cuota de mantenimiento: ${p.mesSeleccionadoTexto} ($${mNeto.toFixed(2)}${esAbono ? ' parc.' : ''})`;
                        } else {
                            updatedPago.observacionesAuto = generarObservacionesPago(mNeto, 'Cuota de mantenimiento:');
                        }
                    } else if (esDesc) {
                        const mNeto = parseFloat(value || 0) * (1 - ((p.descuento || 0) / 100));
                        updatedPago.observacionesAuto = generarObservacionesPago(mNeto, `Descuento pronto pago ${p.descuento || 0}%:`);
                    } else if (esAdeudo) {
                        const mNeto = parseFloat(value || 0);
                        const tieneMes = p.mesSeleccionadoId || (p.mesSeleccionadoTexto && p.mesSeleccionadoTexto !== 'Automático (FIFO)');
                        if (tieneMes) {
                            const montoBase = Number(p.mesSeleccionadoMonto || cuotaMensual);
                            const esAbono = mNeto > 0 && Math.round(mNeto * 100) < Math.round(montoBase * 100);
                            updatedPago.observacionesAuto = `Abono a adeudo: ${p.mesSeleccionadoTexto} ($${mNeto.toFixed(2)}${esAbono ? ' parc.' : ''})`;
                        } else {
                            updatedPago.observacionesAuto = mNeto > 0 ? generarObservacionesPago(mNeto, 'Abono a adeudo:') : '';
                        }
                    } else if (esExtra) {
                        const mExtra = parseFloat(value || 0);
                        if (mExtra > 0) {
                            const cuotaObj = cuotasExtras.find(c => String(c.id) === String(p.cuotaExtraId));
                            const montoTotalCuota = Number(cuotaObj?.monto || 0);
                            const esAbono = montoTotalCuota > 0 && Math.round(mExtra * 100) < Math.round(montoTotalCuota * 100);
                            updatedPago.observacionesAuto = `Pago de Cuota Extraordinaria: ${p.cuotaExtraDesc || ''} ($${mExtra.toFixed(2)}${esAbono ? ' parc.' : ''})`;
                        } else {
                            updatedPago.observacionesAuto = '';
                        }
                    } else if (esRecargo) {
                        const montoRec = parseFloat(value || 0);
                        if (montoRec > 0) {
                            const previewRec = computePaymentPreview(montoRec, adeudoCapitalActual, adeudoInteresesActual, Number(casa?.saldoFavor || 0));
                            const partes = [];
                            if (previewRec.aplicadoIntereses > 0) partes.push(`$${previewRec.aplicadoIntereses.toFixed(2)} aplicado a intereses`);
                            if (previewRec.aplicadoCapital > 0) partes.push(`$${previewRec.aplicadoCapital.toFixed(2)} aplicado a capital`);
                            if (previewRec.saldoFavorGenerado > 0) partes.push(`$${previewRec.saldoFavorGenerado.toFixed(2)} a saldo a favor`);
                            if (previewRec.adeudoNuevo > 0) partes.push(`adeudo restante $${previewRec.adeudoNuevo.toFixed(2)}`);
                            updatedPago.observacionesAuto = partes.length > 0 ? `Recargos e intereses: ${partes.join(', ')}` : '';
                        }
                    } else if (esConceptoSancion(conceptoLower)) {
                        const mSancion = parseFloat(value || 0);
                        if (mSancion > 0 && p.multaAdminDesc) {
                            updatedPago.observacionesAuto = `Pago de Sanción Administrativa: ${p.multaAdminDesc} ($${mSancion.toFixed(2)})`;
                        } else {
                            updatedPago.observacionesAuto = '';
                        }
                    } else if (!esPartidaContableResidente(p)) {
                        updatedPago.observacionesAuto = 'Ingreso de tesorería (No afecta adeudo ni genera saldo a favor)';
                    }
                }

                return updatedPago;
            }
            return p;
        });
        const globalNumCap = parseFloat(montoTotalCap || 0);
        setPagos(actualizarPartidasConExcedente(updatedPagos, globalNumCap));
    };

    const handleSelectMulta = (idOrIndex, pagoId) => {
        if (!idOrIndex) {
            const next = pagos.map((p) => {
                if (p.id === pagoId) {
                    return {
                        ...p,
                        multaAdminId: '',
                        multaAdminDocId: '',
                        multaAdminDesc: '',
                        monto: 0,
                        descuento: 0,
                        observacionesAuto: ''
                    };
                }
                return p;
            });
            const globalNumCap = parseFloat(montoTotalCap || 0);
            setPagos(actualizarPartidasConExcedente(next, globalNumCap));
            return;
        }

        if (!sancionesCasaList || sancionesCasaList.length === 0) return;
        const selected = sancionesCasaList.find((m) => String(m.id) === String(idOrIndex) || String(m.docId) === String(idOrIndex))
            || sancionesCasaList[Number(idOrIndex)]
            || null;
        if (!selected) return;
        const next = pagos.map((p) => {
            if (p.id === pagoId) {
                const montoSel = Number(selected.monto || 0);
                return {
                    ...p,
                    multaAdminId: selected.id,
                    multaAdminDocId: selected.docId || selected.id,
                    multaAdminDesc: selected.descripcion,
                    monto: montoSel,
                    descuento: 0,
                    observacionesAuto: `Pago de Sanción Administrativa: ${selected.descripcion} ($${montoSel.toFixed(2)})`
                };
            }
            return p;
        });
        const globalNumCap = parseFloat(montoTotalCap || 0);
        setPagos(actualizarPartidasConExcedente(next, globalNumCap));
    };




    const handleSelectCuotaExtra = (idOrIndex, pagoId) => {
        const selected = cuotasExtras.find((c) => String(c.id) === String(idOrIndex))
            || cuotasExtras[Number(idOrIndex)]
            || null;
        if (!selected) return;

        const pagada = isCuotaExtraPagadaCompleta(selected, rawMesesData);
        if (pagada) {
            Swal.fire({
                icon: 'warning',
                title: 'Cuota Ya Pagada',
                text: `La cuota extraordinaria "${selected.descripcion}" ya está pagada completamente para esta casa y no se puede volver a cobrar.`
            });

            const next = pagos.map((p) => {
                if (p.id === pagoId) {
                    return {
                        ...p,
                        cuotaExtraId: selected.id,
                        cuotaExtraDesc: selected.descripcion,
                        monto: 0,
                        descuento: 0,
                        observacionesAuto: `Cuota Extraordinaria: ${selected.descripcion} (PAGADA COMPLETA)`
                    };
                }
                return p;
            });
            const globalNumCap = parseFloat(montoTotalCap || 0);
            setPagos(actualizarPartidasConExcedente(next, globalNumCap));
            return;
        }

        // Intentar encontrar el saldo pendiente real en rawMesesData
        const cleanName = String(selected.descripcion || '').replace(/\s+/g, '').substring(0, 15);
        const mesActualStr = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
        const targetId = `EXT-${cleanName}-${mesActualStr}`;

        const docReal = rawMesesData.find(m => m.id === selected.id || m.mesId === selected.id) ||
            rawMesesData.find(m => {
                const matchesIdOrPrefix = (m.id === targetId || m.mesId === targetId) ||
                    (m.id && m.id.startsWith(`EXT-${cleanName}-`)) ||
                    (m.mesId && m.mesId.startsWith(`EXT-${cleanName}-`));
                const est = String(m.estado || '').trim().toLowerCase();
                return matchesIdOrPrefix && (est === 'pendiente' || est === 'adeudo' || est === 'pagado_parcialmente' || est === 'pagado_parcial');
            }) || rawMesesData.find(m => {
                return (m.id === targetId || m.mesId === targetId) ||
                    (m.id && m.id.startsWith(`EXT-${cleanName}-`)) ||
                    (m.mesId && m.mesId.startsWith(`EXT-${cleanName}-`));
            });

        // El monto sugerido será el montoPendiente del documento si existe, o el monto original de la cuota
        const montoInicial = docReal && docReal.montoPendiente !== undefined 
            ? Number(docReal.montoPendiente) 
            : Number(selected.monto || 0);

        const montoTotalCuota = Number(selected.monto || 0);
        const esAbonoExtra = montoTotalCuota > 0 && Math.round(montoInicial * 100) < Math.round(montoTotalCuota * 100);

        const next = pagos.map((p) => {
            if (p.id === pagoId) {
                return {
                    ...p,
                    cuotaExtraId: selected.id,
                    cuotaExtraDesc: selected.descripcion,
                    monto: montoInicial,
                    descuento: 0,
                    observacionesAuto: `Pago de Cuota Extraordinaria: ${selected.descripcion} ($${montoInicial.toFixed(2)}${esAbonoExtra ? ' parc.' : ''})`
                };
            }
            return p;
        });
        const globalNumCap = parseFloat(montoTotalCap || 0);
        setPagos(actualizarPartidasConExcedente(next, globalNumCap));
    };

    // Calcular totales
    const totalMonto = pagos.reduce((sum, p) => sum + parseFloat(p.monto || 0), 0);
    const totalDescuento = pagos.reduce((sum, p) => sum + (parseFloat(p.monto || 0) * (parseFloat(p.descuento || 0) / 100)), 0);
    const montoFinal = Math.max(0, totalMonto - totalDescuento);

    // Log unificado de cálculo de partidas solicitado
    console.log('📊 [ModalRegistroPago] Actualizando Cálculos de Partidas:', {
        totalPartidas: pagos.length,
        totalCapturado: parseFloat(montoTotalCap || 0),
        totalDesglosado: totalMonto,
        descuentoAplicado: totalDescuento,
        montoNetoFinal: montoFinal,
        partidas: pagos.map(p => ({
            id: p.id,
            concepto: p.concepto || 'Sin asignar',
            monto: p.monto,
            idPeriodoAgua: p.periodoAguaId || null,
            idCuotaExtra: p.cuotaExtraId || null
        }))
    });

    const adeudoTotalActual = (casa?.adeudoCapital !== undefined && casa?.adeudoCapital !== null)
        ? (Number(casa?.adeudoCapital ?? 0) + Number(casa?.adeudoIntereses ?? 0))
        : Number(casa?.adeudo ?? casa?.deuda ?? casa?.montoAdeudo ?? 0);
    const mesesAdeudo = Number(casa?.mesesAdeudo ?? 0);
    const tipoNorm = String(interesesType || 'fixed').toLowerCase();
    const tasa = Number(interesesValue || 0) / 100;
    const cuota = Number(cuotaMensual || 0);

    const estimateCapital = () => {
        const hasCapitalField = casa && Object.prototype.hasOwnProperty.call(casa, 'adeudoCapital');
        const capitalField = hasCapitalField ? Number(casa.adeudoCapital || 0) : null;
        if (hasCapitalField && capitalField > 0) return capitalField;
        // Fallback cuando no hay campo o es 0 pero sí hay adeudo y meses
        if (adeudoTotalActual > 0 && mesesAdeudo > 0 && cuota > 0) {
            return Math.min(adeudoTotalActual, mesesAdeudo * cuota);
        }
        return capitalField || 0;
    };

    const estimateIntereses = (capitalEstimado) => {
        const hasInteresesField = casa && Object.prototype.hasOwnProperty.call(casa, 'adeudoIntereses');
        const interesesField = hasInteresesField ? Number(casa.adeudoIntereses || 0) : null;
        // Si el campo existe en Firestore, siempre usarlo (respeta el 0 explícito)
        if (hasInteresesField) return interesesField;
        // Fallback solo si el campo NO existe en el documento (casas anteriores sin el campo)
        if (adeudoTotalActual > 0 && mesesAdeudo > 0 && cuota > 0) {
            if (tipoNorm === 'fixed' || tipoNorm === 'fixed_monthly' || tipoNorm === 'fijo' || tipoNorm === 'monto_fijo') {
                return mesesAdeudo * cuota * tasa;
            }
            if (tipoNorm === 'percent_simple' || tipoNorm === 'simple' || tipoNorm === 'porcentaje_simple') {
                return mesesAdeudo * cuota * tasa;
            }
            // Compound o desconocido: resto del adeudo
            return Math.max(0, adeudoTotalActual - capitalEstimado);
        }
        return 0;
    };

    const adeudoCapitalActual = estimateCapital();
    const adeudoInteresesActual = estimateIntereses(adeudoCapitalActual);

    const computePaymentPreview = (pago = 0, adeudoCap = 0, adeudoInt = 0, saldoFavor = 0) => {
        let restante = pago;
        let aplicadoIntereses = 0;
        let aplicadoCapital = 0;
        let nuevoSaldoFavor = saldoFavor;

        if (adeudoCap <= 0 && adeudoInt <= 0) {
            nuevoSaldoFavor = saldoFavor + restante;
            restante = 0;
        } else {
            if (adeudoInt > 0 && restante > 0) {
                aplicadoIntereses = Math.min(adeudoInt, restante);
                restante -= aplicadoIntereses;
            }
            if (adeudoCap > 0 && restante > 0) {
                aplicadoCapital = Math.min(adeudoCap, restante);
                restante -= aplicadoCapital;
            }
            if (restante > 0) {
                nuevoSaldoFavor = saldoFavor + restante;
                restante = 0;
            }
        }

        const adeudoInteresesNuevo = Math.max(0, adeudoInt - aplicadoIntereses);
        const adeudoCapitalNuevo = Math.max(0, adeudoCap - aplicadoCapital);
        const adeudoNuevo = adeudoInteresesNuevo + adeudoCapitalNuevo;

        return {
            aplicadoIntereses,
            aplicadoCapital,
            saldoFavorGenerado: Math.max(0, nuevoSaldoFavor - saldoFavor),
            adeudoInteresesNuevo,
            adeudoCapitalNuevo,
            adeudoNuevo,
        };
    };

    // Solo los conceptos contables de mantenimiento/recargos (Categoría A) entran a amortizar capital/intereses o a generar saldo a favor
    const montoContableMantto = pagos
        .filter(p => esConceptoMantenimiento(p.concepto) || String(p.concepto || '').toLowerCase().includes('recargo') || String(p.concepto || '').toLowerCase().includes('interes'))
        .reduce((sum, p) => {
            const monto = parseFloat(p.monto || 0);
            const desc = monto * (parseFloat(p.descuento || 0) / 100);
            return sum + Math.max(0, monto - desc);
        }, 0);

    const preview = computePaymentPreview(montoContableMantto, adeudoCapitalActual, adeudoInteresesActual, Number(casa?.saldoFavor || 0));
    
    // Determinar si hay un bloqueo por falta de configuración (Cuotas o Multas)
    const blockingError = (() => {
        for (const p of pagos) {
            const conceptoLower = String(p.concepto || '').toLowerCase();
            const esCuotaExtra = conceptoLower.includes('cuota') && conceptoLower.includes('extraordinaria');
            const esMultaAdmin = (conceptoLower.includes('multa') || conceptoLower.includes('sancion') || conceptoLower.includes('sansion')) && (conceptoLower.includes('administr') || conceptoLower.includes('sancion') || conceptoLower.includes('sansion') || conceptoLower.includes('multa'));

            if (esCuotaExtra && (!cuotasExtras || cuotasExtras.length === 0)) {
                return { type: 'cuota', message: 'No hay cuotas extraordinarias configuradas en el sistema.' };
            }
            if (esMultaAdmin) {
                if (!isMultasEnabled) return { type: 'multa_disabled', message: 'Las sanciones administrativas están desactivadas en la configuración financiera.' };
                if (!sancionesCasaList || sancionesCasaList.length === 0) return { type: 'multa_empty', message: 'Este domicilio no tiene sanciones administrativas pendientes asignadas.' };
            }
        }
        return null;
    })();

    const hasBlockingFormError = Boolean(blockingError);

    // Enviar formulario
    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMessage('');

        // Validar que al menos un pago tiene monto o es un pago de agua cubierto por saldo a favor
        // Validar que al menos un pago tiene monto
        if (pagos.every(p => parseFloat(p.monto || 0) === 0)) {
            setErrorMessage('⚠️ Monto: Debe ingresar al menos un monto de pago.');
            return;
        }

        // Validar que cada pago tenga los campos requeridos
        for (let i = 0; i < pagos.length; i++) {
            const pago = pagos[i];

            if (!pago.monto || parseFloat(pago.monto) <= 0) {
                setErrorMessage(`⚠️ Pago ${i + 1}: Monto es obligatorio y debe ser mayor a 0.`);
                return;
            }

            if (!pago.concepto || !pago.concepto.trim()) {
                setErrorMessage(`⚠️ Pago ${i + 1}: Concepto es obligatorio.`);
                return;
            }

            if (!pago.fechaPago) {
                setErrorMessage(`⚠️ Pago ${i + 1}: Fecha de pago es obligatoria.`);
                return;
            }

            if (!pago.metodoPago || !pago.metodoPago.trim()) {
                setErrorMessage(`⚠️ Pago ${i + 1}: Método de pago es obligatorio.`);
                return;
            }

            // Validar concepto "Descuentos / Pronto Pago"
            const conceptoLower = pago.concepto.toLowerCase();
            if (conceptoLower.includes('descuento') || conceptoLower.includes('pronto pago')) {

                // Validar estado al corriente
                if (adeudoTotal > 0) {
                    setErrorMessage(`⚠️ Pago ${i + 1}: "Descuentos / Pronto Pago" solo aplica para residentes AL CORRIENTE.`);
                    return;
                }

                // Validar que el descuento esté habilitado en la configuración
                if (!descuentoEnabled) {
                    setErrorMessage(`⚠️ Pago ${i + 1}: El descuento por pronto pago no está habilitado en la configuración financiera.`);
                    return;
                }

                // Validar que hay descuento configurado con un porcentaje
                if (!descuentoConfiguracion || descuentoConfiguracion === 0) {
                    setErrorMessage(`⚠️ Pago ${i + 1}: No hay descuento por pronto pago configurado en el sistema.`);
                    return;
                }

                // Validar monto igual a cuota mensual
                if (cuotaMensual > 0 && parseFloat(pago.monto) !== cuotaMensual) {
                    setErrorMessage(`⚠️ Pago ${i + 1}: El monto debe ser igual a la cuota mensual ($${cuotaMensual.toFixed(2)}).`);
                    return;
                }

                // Validar día de corte
                if (diaCorte !== null && diaCorte !== undefined) {
                    const hoy = new Date();
                    const diaActual = hoy.getDate();

                    if (diaActual > diaCorte) {
                        setErrorMessage(`⚠️ Pago ${i + 1}: Ya pasó el día de corte (día ${diaCorte}). Use "Cuota de Mantenimiento".`);
                        return;
                    }
                }
            }

            // Validación para Sanciones Administrativas
            if (esConceptoSancion(conceptoLower)) {
                if (!sancionesCasaList || sancionesCasaList.length === 0) {
                    setErrorMessage(`⚠️ Pago ${i + 1}: Este domicilio no tiene sanciones administrativas pendientes asignadas.`);
                    return;
                }
                if (!pago.multaAdminDesc && !pago.multaAdminId) {
                    setErrorMessage(`⚠️ Pago ${i + 1}: Selecciona la sanción administrativa asignada a este domicilio.`);
                    return;
                }
                const found = sancionesCasaList.find((m) => m.descripcion === pago.multaAdminDesc || String(m.id) === String(pago.multaAdminId) || String(m.docId) === String(pago.multaAdminId));
                if (!found) {
                    setErrorMessage(`⚠️ Pago ${i + 1}: La sanción seleccionada no está asignada a este domicilio.`);
                    return;
                }
                if (Number(pago.monto || 0) <= 0) {
                    setErrorMessage(`⚠️ Pago ${i + 1}: El monto asignado a la sanción debe ser mayor a $0.00.`);
                    return;
                }
                if (Number(pago.monto || 0) !== Number(found.monto || 0)) {
                    setErrorMessage(`⚠️ Pago ${i + 1}: El monto debe coincidir con la sanción asignada ($${Number(found.monto || 0).toFixed(2)}).`);
                    return;
                }
            }

            // Validación para Cuotas Extraordinarias
            if (conceptoLower.includes('cuota') && conceptoLower.includes('extraordinaria')) {
                if (!cuotasExtras || cuotasExtras.length === 0) {
                    setErrorMessage(`⚠️ Pago ${i + 1}: No hay cuotas extraordinarias configuradas en el sistema.`);
                    return;
                }
                if (!pago.cuotaExtraDesc) {
                    setErrorMessage(`⚠️ Pago ${i + 1}: Selecciona la cuota extraordinaria aplicada.`);
                    return;
                }
                const found = cuotasExtras.find((c) => c.descripcion === pago.cuotaExtraDesc || String(c.id) === String(pago.cuotaExtraId));

                // VALIDACIÓN DE SEGURIDAD FINAL E HISTORIAL
                const cleanName = String(pago.cuotaExtraDesc || '').replace(/\s+/g, '').substring(0, 15);
                const mesActualStr = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
                const targetId = `EXT-${cleanName}-${mesActualStr}`;
                
                // Buscar si existe en rawMesesData (puede ser histórica) priorizando estado pendiente
                const docReal = (found && rawMesesData.find(m => m.id === found.id || m.mesId === found.id)) ||
                    rawMesesData.find(m => {
                        const matchesIdOrPrefix = (m.id === targetId || m.mesId === targetId) ||
                            (m.id && m.id.startsWith(`EXT-${cleanName}-`)) ||
                            (m.mesId && m.mesId.startsWith(`EXT-${cleanName}-`));
                        return matchesIdOrPrefix && m.estado === 'pendiente';
                    }) || rawMesesData.find(m => {
                        return (m.id === targetId || m.mesId === targetId) ||
                            (m.id && m.id.startsWith(`EXT-${cleanName}-`)) ||
                            (m.mesId && m.mesId.startsWith(`EXT-${cleanName}-`));
                    });
                
                const isPaid = docReal && (docReal.estado === 'pagado_completo' || docReal.estado === 'pagado');
                const isPendingCutoff = !docReal; // Si no hay doc, es pendiente de corte o no aplica

                if (isPaid) {
                    setErrorMessage(`⚠️ Pago ${i + 1}: La cuota "${pago.cuotaExtraDesc}" ya está pagada.`);
                    setIsSubmitting(false);
                    return;
                }

                if (isPendingCutoff) {
                    setErrorMessage(`⚠️ Pago ${i + 1}: La cuota "${pago.cuotaExtraDesc}" está pendiente de corte o no aplica.`);
                    setIsSubmitting(false);
                    return;
                }

                // VALIDACIÓN DEL MONTO DEL PAGO (PERMITIR PARCIALIDADES/ABONOS)
                if (found) {
                    const maximoPermitido = docReal.montoPendiente !== undefined ? docReal.montoPendiente : (docReal.montoOriginal || found.monto || 0);
                    const montoPago = Number(pago.monto || 0);
                    if (montoPago <= 0) {
                        setErrorMessage(`⚠️ Pago ${i + 1}: El monto asignado a la cuota extraordinaria debe ser mayor a $0.00.`);
                        return;
                    }
                    if (montoPago > Number(maximoPermitido)) {
                        setErrorMessage(`⚠️ Pago ${i + 1}: El monto a pagar ($${montoPago.toFixed(2)}) no puede ser mayor al saldo pendiente de la cuota ($${Number(maximoPermitido).toFixed(2)}).`);
                        return;
                    }
                }
            }

            // Referencia y comprobante
            const tieneReferencia = Boolean(pago.referencia && pago.referencia.trim());

            if (!tieneReferencia) {
                setErrorMessage(`⚠️ Pago ${i + 1}: Referencia/Operación es obligatoria.`);
                return;
            }
            // El comprobante ahora es opcional.
        }

        setIsSubmitting(true);

        const pagosProcesados = pagos.map(p => {
                const monto = parseFloat(p.monto || 0);
                const descuentoValor = monto * (parseFloat(p.descuento || 0) / 100);
                const montoNeto = monto - descuentoValor;

                const conceptoLower = String(p.concepto || '').toLowerCase();
                const esPagoAnual = conceptoLower.includes('pago') && conceptoLower.includes('anual') && conceptoLower.includes('cuota') && conceptoLower.includes('mantto');
                const esDescuentoProntoPago = conceptoLower.includes('descuento') || conceptoLower.includes('pronto pago');
                const esRecargosIntereses = conceptoLower.includes('recargo') || conceptoLower.includes('interes');
                const esCuotaMantenimiento = conceptoLower.includes('cuota') && conceptoLower.includes('mantenimiento');

                const adeudoActual = Number(casa?.adeudo || 0);
                const tieneDescuentoProntoPago = esDescuentoProntoPago || (descuentoEnabled && descuentoConfiguracion > 0);

                    // Generar desglose mensual usando la función auxiliar (marca esParcial en cada fila)
                let desgloseMensual = null;
                let anioPagoAnual = null;

                const generaDesglose = esPagoAnual || esDescuentoProntoPago || esCuotaMantenimiento;

                if (generaDesglose && cuotaMensual > 0) {
                    const fechaPagoDate = p.fechaPago ? new Date(p.fechaPago) : new Date();
                    const mesesDelAnio = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

                    let saldoCreditoParaDesglose = Number(casa?.saldoFavor || 0);
                    let dineroNuevoParaDesglose = montoNeto;
                    let indiceMesSimulado = primerMesDeuda ? primerMesDeuda.mes - 1 : fechaPagoDate.getMonth();
                    let anioSimulado = primerMesDeuda ? primerMesDeuda.año : fechaPagoDate.getFullYear();

                    desgloseMensual = [];

                    if (p.mesSeleccionadoId && mesesPendientesData && mesesPendientesData.length > 0) {
                        const mesObj = mesesPendientesData.find(m => m.id === p.mesSeleccionadoId);
                        if (mesObj) {
                            const idx = (parseMesNumero(mesObj.mesNumero || mesObj.mes) - 1) % 12;
                            const anioStr = Number(mesObj.año || mesObj.anio);
                            const pendienteMes = Number(mesObj.montoPendiente) || cuotaMensual;
                            desgloseMensual = [{
                                mes: mesesDelAnio[idx],
                                mesNumero: idx + 1,
                                año: anioStr,
                                importe: montoNeto,
                                esParcial: montoNeto < pendienteMes
                            }];
                            anioPagoAnual = anioStr;
                        }
                    } else {
                        if (mesesPendientesData && mesesPendientesData.length > 0) {
                            for (const mesData of mesesPendientesData) {
                                if (dineroNuevoParaDesglose <= 0 && saldoCreditoParaDesglose <= 0) break;
                                
                                // Omitir meses asignados a otras partidas específicas
                                const asignadoEnOtra = pagos.some(other => other.id !== p.id && other.mesSeleccionadoId === mesData.id);
                                if (asignadoEnOtra) continue;

                                const pendienteMes = Number(mesData.montoPendiente) || cuotaMensual;
                                if (pendienteMes <= 0) continue;
                                
                                const idx = (parseMesNumero(mesData.mesNumero || mesData.mes) - 1) % 12;
                                const anioStr = Number(mesData.año || mesData.anio);

                                // ¿Cuánto de este mes se cubre con saldo a favor?
                                const cubiertoPorSaldo = Math.min(pendienteMes, saldoCreditoParaDesglose);
                                saldoCreditoParaDesglose -= cubiertoPorSaldo;
                                const pendienteTrasSaldo = pendienteMes - cubiertoPorSaldo;

                                if (pendienteTrasSaldo > 0) {
                                    // Lo que sobre, se cubre con dinero nuevo
                                    const cubiertoPorNuevo = Math.min(pendienteTrasSaldo, dineroNuevoParaDesglose);
                                    if (cubiertoPorNuevo > 0) {
                                        desgloseMensual.push({
                                            mes: mesesDelAnio[idx],
                                            mesNumero: idx + 1,
                                            año: anioStr,
                                            importe: cubiertoPorNuevo,
                                            esParcial: cubiertoPorNuevo < pendienteTrasSaldo
                                        });
                                        dineroNuevoParaDesglose -= cubiertoPorNuevo;
                                    }
                                }
                                indiceMesSimulado = (idx + 1) % 12;
                                anioSimulado = (idx === 11) ? anioStr + 1 : anioStr;
                            }
                        }

                        while (dineroNuevoParaDesglose > 0) {
                            // Primero agotar el saldo a favor restante en meses futuros
                            if (saldoCreditoParaDesglose >= cuotaMensual) {
                                saldoCreditoParaDesglose -= cuotaMensual;
                            } else {
                                const pendienteMes = cuotaMensual - saldoCreditoParaDesglose;
                                saldoCreditoParaDesglose = 0;
                                
                                const cubiertoPorNuevo = Math.min(pendienteMes, dineroNuevoParaDesglose);
                                if (cubiertoPorNuevo > 0) {
                                    desgloseMensual.push({
                                        mes: mesesDelAnio[indiceMesSimulado],
                                        mesNumero: indiceMesSimulado + 1,
                                        año: anioSimulado,
                                        importe: cubiertoPorNuevo,
                                        esParcial: cubiertoPorNuevo < pendienteMes
                                    });
                                    dineroNuevoParaDesglose -= cubiertoPorNuevo;
                                }
                            }
                            indiceMesSimulado++;
                            if (indiceMesSimulado > 11) { indiceMesSimulado = 0; anioSimulado++; }
                        }
                    }

                    if (desgloseMensual.length === 0) desgloseMensual = null;
                    anioPagoAnual = (desgloseMensual && desgloseMensual.length > 0) ? desgloseMensual[0].año : fechaPagoDate.getFullYear();
                }

                const tieneAdeudoPrevio = Number(casa?.adeudo || 0) > 0 || Number(casa?.adeudoCapital || 0) > 0;
                const esPagoAnticipo = !tieneAdeudoPrevio && generaDesglose && Array.isArray(desgloseMensual) && desgloseMensual.length > 0;

                // Calcular meses pagados adelantado solo si realmente es anticipo
                const mesesPagadosAdelantado = (esPagoAnticipo && desgloseMensual) ? desgloseMensual.length : 0;

                const esAguaPago = conceptoLower.includes('agua') || conceptoLower.includes('cea');
                const obsAuto = (p.observacionesAuto || '').trim();
                const obsManualItem = (p.observaciones || '').trim();
                const obsGeneralText = (observacionesGeneral || '').trim();

                let observacionesFinales = '';
                if (pagos.length === 1) {
                    const textoManual = obsGeneralText || obsManualItem;
                    if (obsAuto && textoManual) {
                        observacionesFinales = textoManual.includes(obsAuto) ? textoManual : `${obsAuto}\n${textoManual}`;
                    } else {
                        observacionesFinales = textoManual || obsAuto || '';
                    }
                } else {
                    if (obsAuto && obsManualItem) {
                        observacionesFinales = obsManualItem.includes(obsAuto) ? obsManualItem : `${obsAuto}\n${obsManualItem}`;
                    } else {
                        observacionesFinales = obsManualItem || obsAuto || '';
                    }
                }

                if (esAguaPago) {
                    const adeudoPrevio = Number(casa?.adeudo || casa?.deudaTotal || 0);
                    const adeudoPostPago = Math.max(0, Math.round((adeudoPrevio - montoNeto) * 100) / 100);

                    let notaAgua = '';
                    if (adeudoPostPago > 0) {
                        const formattedAdeudo = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(adeudoPostPago);
                        notaAgua = `Pago de cuota de consumo de agua potable liquidado. Cuenta con adeudo histórico pendiente (${formattedAdeudo}). El pago de consumo de agua potable no libera de adeudos anteriores.`;
                    } else {
                        notaAgua = 'Consumo de Agua Potable - AL CORRIENTE';
                    }

                    const textoManualAgua = pagos.length === 1 ? (obsGeneralText || obsManualItem) : obsManualItem;
                    if (textoManualAgua) {
                        observacionesFinales = `${notaAgua}\n${textoManualAgua}`;
                    } else {
                        observacionesFinales = notaAgua;
                    }
                }

                let detallePartida = p.detalle || '';
                if (!detallePartida && p.mesSeleccionadoTexto) {
                    const mesMontoPendiente = Number(p.mesSeleccionadoMonto || cuotaMensual);
                    const esParcial = montoNeto > 0 && Math.round(montoNeto * 100) < Math.round(mesMontoPendiente * 100);
                    detallePartida = `Mes: ${p.mesSeleccionadoTexto}${esParcial ? ' (Parcial)' : ''}`;
                } else if (!detallePartida && (p.cuotaExtraDesc || (p.concepto && String(p.concepto).toLowerCase().includes('extra')))) {
                    const cuotaObj = cuotasExtras.find(c => String(c.id) === String(p.cuotaExtraId));
                    const cuotaMontoPendiente = Number(cuotaObj?.monto || 0);
                    const esParcialExtra = cuotaMontoPendiente > 0 && Math.round(montoNeto * 100) < Math.round(cuotaMontoPendiente * 100);
                    const rawDesc = cuotaObj?.descripcion || p.cuotaExtraDesc || 'Cuota Extraordinaria';
                    const descLimpia = String(rawDesc || '')
                        .replace(/^cuota\s*extra\s*:?\s*/i, '')
                        .replace(/^cuota\s*extraordinaria\s*:?\s*/i, '')
                        .trim();
                    detallePartida = `Cuota Extra: ${descLimpia || rawDesc}${esParcialExtra ? ' (Parcial)' : ''}`;
                } else if (!detallePartida && desgloseMensual && desgloseMensual.length > 0) {
                    detallePartida = desgloseMensual.map(d => `${d.mes} ${d.año}${d.esParcial ? ' (Parcial)' : ''}`).join(', ');
                } else if (!detallePartida && p.periodoAguaId && periodosAguaPendientes) {
                    const pAgua = periodosAguaPendientes.find(a => a.id === p.periodoAguaId);
                    if (pAgua && pAgua.periodoLectura) detallePartida = `Periodo: ${pAgua.periodoLectura}`;
                }

                return {
                    monto,
                    concepto: p.concepto,
                    detalle: detallePartida,
                    descuento: descuentoValor,
                    descuentoPorcentaje: parseFloat(p.descuento || 0),
                    montoNeto,
                    mesesPagadosAdelantado,
                    fechaPago: p.fechaPago,
                    metodoPago: p.metodoPago,
                    referencia: p.referencia,
                    observaciones: observacionesFinales,
                    comprobante: p.comprobante,
                    multaAdministrativa: p.multaAdminDesc || null,
                    multaAdminId: p.multaAdminId || null,
                    multaAdminDocId: p.multaAdminDocId || null,
                    multaAdminDesc: p.multaAdminDesc || null,
                    cuotaExtraordinaria: p.cuotaExtraDesc || null,
                    cuotaExtraDesc: p.cuotaExtraDesc || null,
                    cuotaExtraId: p.cuotaExtraId || null,
                    mesSeleccionadoId: p.mesSeleccionadoId || null,
                    mesSeleccionadoTexto: p.mesSeleccionadoTexto || null,
                    mesSeleccionadoMonto: p.mesSeleccionadoMonto || 0,
                    esPagoAnual: esPagoAnual || false,
                    esPagoAnticipo,
                    desgloseMensual,
                    anioPagoAnual,
                    saldoFavorAnterior: Number(casa?.saldoFavor || 0),
                    saldoFavorConsumido: Number(p.saldoFavorConsumido || 0),
                    periodoAguaId: p.periodoAguaId || null,
                    esPagoAguaPotable: p.periodoAguaId ? true : false
                };
            });

        const globalNumCap = parseFloat(montoTotalCap || 0);
        let finalPagosList = [...pagosProcesados];
        let finalTotalMonto = totalMonto;
        let finalMontoFinal = montoFinal;

        if (globalNumCap > totalMonto) {
            const excedente = Math.round((globalNumCap - totalMonto) * 100) / 100;
            if (excedente > 0) {
                const conceptoExcedente = 'Cuota de Mantenimiento';
                let desgloseExcedente = null;

                if (cuotaMensual > 0) {
                    const fechaPagoDate = new Date();
                    const mesesDelAnio = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
                    let dineroNuevo = excedente;
                    let idxSimulado = primerMesDeuda ? primerMesDeuda.mes - 1 : fechaPagoDate.getMonth();
                    let anioSimulado = primerMesDeuda ? primerMesDeuda.año : fechaPagoDate.getFullYear();
                    desgloseExcedente = [];

                    if (mesesPendientesData && mesesPendientesData.length > 0) {
                        for (const mesData of mesesPendientesData) {
                            if (dineroNuevo <= 0) break;
                            const pendienteMes = Number(mesData.montoPendiente) || cuotaMensual;
                            if (pendienteMes <= 0) continue;
                            const idx = (parseMesNumero(mesData.mesNumero || mesData.mes) - 1) % 12;
                            const anioStr = Number(mesData.año || mesData.anio);
                            const cubierto = Math.min(pendienteMes, dineroNuevo);
                            if (cubierto > 0) {
                                desgloseExcedente.push({
                                    mes: mesesDelAnio[idx],
                                    mesNumero: idx + 1,
                                    año: anioStr,
                                    importe: cubierto,
                                    esParcial: cubierto < pendienteMes
                                });
                                dineroNuevo -= cubierto;
                            }
                        }
                    }
                    while (dineroNuevo > 0) {
                        const cubierto = Math.min(cuotaMensual, dineroNuevo);
                        if (cubierto > 0) {
                            desgloseExcedente.push({
                                mes: mesesDelAnio[idxSimulado],
                                mesNumero: idxSimulado + 1,
                                año: anioSimulado,
                                importe: cubierto,
                                esParcial: cubierto < cuotaMensual
                            });
                            dineroNuevo -= cubierto;
                        }
                        idxSimulado++;
                        if (idxSimulado > 11) { idxSimulado = 0; anioSimulado++; }
                    }
                }

                let detalleExcedente = '';
                if (desgloseExcedente && desgloseExcedente.length > 0) {
                    detalleExcedente = desgloseExcedente.map(d => `${d.mes} ${d.año}${d.esParcial ? ' (Parcial)' : ''}`).join(', ');
                }

                finalPagosList.push({
                    monto: excedente,
                    concepto: conceptoExcedente,
                    detalle: detalleExcedente,
                    descuento: 0,
                    descuentoPorcentaje: 0,
                    montoNeto: excedente,
                    mesesPagadosAdelantado: desgloseExcedente ? desgloseExcedente.length : 0,
                    fechaPago: fechaPagoGeneral,
                    metodoPago: metodoPagoGeneral,
                    referencia: referenciaGeneral,
                    observaciones: `Excedente de $${excedente.toFixed(2)} asignado a ${conceptoExcedente}`,
                    comprobante: comprobanteGeneral,
                    multaAdministrativa: null,
                    cuotaExtraordinaria: null,
                    esPagoAnual: false,
                    esPagoAnticipo: desgloseExcedente && desgloseExcedente.length > 0,
                    desgloseMensual: desgloseExcedente,
                    saldoFavorAnterior: Number(casa?.saldoFavor || 0),
                    saldoFavorConsumido: 0,
                    periodoAguaId: null,
                    esPagoAguaPotable: false
                });

                finalTotalMonto = globalNumCap;
                finalMontoFinal = globalNumCap;
            }
        }

        try {
            await onConfirm({
                pagos: finalPagosList,
                folioBase: nextFolioNumber,
                folioSerie: receiptConfig?.folioSerie || 'R',
                totalMonto: finalTotalMonto,
                totalDescuento,
                montoFinal: finalMontoFinal,
                saldoFavorGenerado: preview.saldoFavorGenerado,
                observaciones: (observacionesGeneral || '').trim() || (generarLeyendaCoberturaPago() || '')
            });
        } catch (confirmError) {
            console.error("Error al procesar el pago desde el modal:", confirmError);
            setErrorMessage('⚠️ Error al registrar el pago: ' + (confirmError.message || 'Intente nuevamente.'));
        } finally {
            setIsSubmitting(false);
        }
        // onClose(); // Se delega el cierre al componente padre una vez confirmado el pago en la ventana emergente.
    };

    const metodosDePago = [
        'Transferencia Bancaria', 'Tarjeta de Crédito', 'Tarjeta de Débito',
        'PayPal', 'Mercado Pago', 'Efectivo', 'Pago Cajero', 'Otros'
    ];

    const sanitizeNumberInput = (value, allowDecimal = true) => {
        const regex = allowDecimal ? /[^0-9.]/g : /[^0-9]/g;
        return (value || '').replace(regex, '');
    };

    const preventNonNumeric = (evt) => {
        const { data, inputType } = evt.nativeEvent;
        if (inputType === 'deleteContentBackward' || inputType === 'deleteContentForward') return;
        if (!data) return;
        if (!/^[0-9.]$/.test(data)) {
            evt.preventDefault();
        }
    };

    const hasCapturedData = () => {
        return pagos.some(p => (
            parseFloat(p.monto || 0) > 0 ||
            (p.concepto && p.concepto.trim()) ||
            parseFloat(p.descuento || 0) > 0 ||
            (p.referencia && p.referencia.trim()) ||
            (p.observaciones && p.observaciones.trim()) ||
            p.comprobante ||
            (p.metodoPago && p.metodoPago !== 'Transferencia Bancaria')
        )) || pagos.length > 1;
    };

    const handleRequestClose = () => {
        if (hasCapturedData()) {
            setShowConfirmClose(true);
            return;
        }
        onClose();
    };

    const confirmCloseModal = () => {
        setShowConfirmClose(false);
        onClose();
    };

    const cancelCloseModal = () => {
        setShowConfirmClose(false);
    };

    return (
        <div
            className="pago-modal-wrapper"
            data-css-version="overlay-v3"
        >
            <div className="pago-modal-content">
                <button onClick={handleRequestClose} className="modal-close-btn"><FaTimes /></button>
                <div className="modal-header">
                    <h2>Registrar Pago - Casa {casa.numero}</h2>
                    <p className="modal-resident-name">{nombrePropietario}</p>
                    <div className="modal-debt-info" style={{ backgroundColor: 'rgba(255, 255, 255, 0.92)', border: '1px solid rgba(255, 255, 255, 0.3)', padding: '6px 12px', borderRadius: '10px' }}>
                        {adeudoTotalGlobal > 0 ? (
                            <>
                                <span className="debt-label" style={{ color: '#dc2626', fontWeight: '800' }}>Deuda Total:</span>
                                <span className="debt-amount" style={{ color: '#dc2626', fontWeight: '800' }}>${adeudoTotalGlobal.toFixed(2)}</span>
                            </>
                        ) : saldoFavorActual > 0 ? (
                            <>
                                <span className="debt-label" style={{ color: '#16a34a', fontWeight: '800' }}>Saldo a Favor:</span>
                                <span className="debt-amount" style={{ color: '#16a34a', fontWeight: '800' }}>${saldoFavorActual.toFixed(2)}</span>
                            </>
                        ) : (
                            <span className="badge badge-success" style={{ background: '#dcfce7', color: '#166534', padding: '6px 12px', borderRadius: '20px', fontWeight: '600' }}>
                                Residente al Corriente
                            </span>
                        )}
                    </div>
                    {nextFolio && (
                        <div className="modal-folio-info">
                            <span className="folio-label">Folio del recibo:</span>
                            <span className="folio-number">{nextFolio}</span>
                        </div>
                    )}
                </div>

                {errorMessage && (
                    <div className="form-error-message">
                        <FaExclamationTriangle /> {errorMessage}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="form-layout">
                        <div className="form-column form-column--primary">
                            {/* Card de Datos Generales de la Transacción */}
                            <div className="pago-card" style={{ marginBottom: '20px', border: '1px solid #cbd5e1', borderRadius: '12px', background: '#f8fafc', padding: '16px' }}>
                                <div className="pago-header" style={{ marginBottom: '14px', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px' }}>
                                    <h4 style={{ margin: 0, color: '#1e293b', fontSize: '1.05rem', fontWeight: 700 }}>
                                        💳 Datos Generales de la Transacción
                                    </h4>
                                </div>

                                <div className="form-grid">
                                    <div className="form-group">
                                        <label className="form-label-row">Monto Total Recibido ($) <span className="help-icon" title="Monto total cobrado o transferido por el residente">?</span></label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            inputMode="decimal"
                                            pattern="[0-9]*[.,]?[0-9]*"
                                            value={montoTotalCap}
                                            onChange={e => {
                                                const clean = sanitizeNumberInput(e.target.value, true);
                                                setMontoTotalCap(clean);
                                                setPagos([{
                                                    id: 1,
                                                    monto: 0,
                                                    concepto: '',
                                                    descuento: 0,
                                                    fechaPago: fechaPagoGeneral,
                                                    metodoPago: metodoPagoGeneral,
                                                    referencia: referenciaGeneral,
                                                    observaciones: observacionesGeneral,
                                                    observacionesAuto: '',
                                                    periodoAguaId: null
                                                }]);
                                            }}
                                            onBeforeInput={preventNonNumeric}
                                            placeholder="0.00"
                                            style={{ fontWeight: '700', fontSize: '1.05rem', color: '#0f172a' }}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label-row">Fecha del Pago <span className="help-icon" title="Día en que se recibió el pago">?</span></label>
                                        <input
                                            type="date"
                                            value={fechaPagoGeneral}
                                            onChange={e => {
                                                setFechaPagoGeneral(e.target.value);
                                                setPagos(pagos.map(p => ({ ...p, fechaPago: e.target.value })));
                                            }}
                                        />
                                    </div>
                                </div>

                                <div className="form-grid">
                                    <div className="form-group">
                                        <label className="form-label-row">Método de Pago <span className="help-icon" title="Selecciona el método utilizado">?</span></label>
                                        <select
                                            value={metodoPagoGeneral}
                                            onChange={e => {
                                                setMetodoPagoGeneral(e.target.value);
                                                setPagos(pagos.map(p => ({ ...p, metodoPago: e.target.value })));
                                            }}
                                        >
                                            {metodosDePago.map(metodo => <option key={metodo} value={metodo}>{metodo}</option>)}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label-row">Referencia/Operación <span className="help-icon" title="Número o código alfanumérico de la operación">?</span></label>
                                        <input
                                            type="text"
                                            value={referenciaGeneral}
                                            onChange={e => {
                                                setReferenciaGeneral(e.target.value);
                                                setPagos(pagos.map(p => ({ ...p, referencia: e.target.value })));
                                            }}
                                            placeholder="Ej: REF123ABC o #12345"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Sección de Desglose por Conceptos */}
                            <div className="pago-card" style={{ border: '1px solid #7c3aed', borderRadius: '12px', background: '#ffffff', padding: '16px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px' }}>
                                    <h4 style={{ margin: 0, color: '#6d28d9', fontSize: '1.05rem', fontWeight: 700 }}>
                                        📊 Desglose de Conceptos (Multi-Caja)
                                    </h4>
                                    <span style={{ fontSize: '0.8rem', background: '#f3e8ff', color: '#6b21a8', padding: '4px 10px', borderRadius: '12px', fontWeight: 600 }}>
                                        {pagos.length} {pagos.length === 1 ? 'partida' : 'partidas'}
                                    </span>
                                </div>

                                <div className="pagos-container">
                                    {pagos.map((pago, index) => (
                                        <div key={pago.id} className="pago-card" style={{ marginBottom: '16px', background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: '8px', padding: '14px' }}>
                                            <div className="pago-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                                <h5 style={{ margin: 0, color: '#581c87', fontWeight: 700 }}>
                                                    Partida #{index + 1}: {pago.concepto || 'Seleccionar concepto...'}
                                                </h5>
                                                {pagos.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemovePago(pago.id)}
                                                        style={{ background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5', borderRadius: '6px', padding: '4px 8px', fontSize: '0.78rem', cursor: 'pointer', fontWeight: 600 }}
                                                    >
                                                        Eliminar partida
                                                    </button>
                                                )}
                                            </div>

                                            <div className="form-grid">
                                                <div className="form-group">
                                                    <label className="form-label-row">Concepto de Pago <span className="help-icon" title="Selecciona qué concepto aplica a este desglose">?</span></label>
                                                    <select
                                                        value={pago.concepto}
                                                        onChange={e => handleUpdatePago(pago.id, 'concepto', e.target.value)}
                                                    >
                                                        <option value="">Seleccionar concepto...</option>
                                                        {conceptos.map((concepto, idx) => {
                                                            const esSanc = esConceptoSancion(concepto);
                                                            const sinSanciones = esSanc && (!sancionesCasaList || sancionesCasaList.length === 0);
                                                            return (
                                                                <option key={idx} value={concepto} disabled={sinSanciones}>
                                                                    {concepto}{sinSanciones ? ' (Sin sanciones asignadas)' : ''}
                                                                </option>
                                                            );
                                                        })}
                                                    </select>
                                                    {(() => {
                                                        const cLower = String(pago.concepto || '').toLowerCase();
                                                        const esMant = cLower.includes('cuota') && cLower.includes('mantenimiento') && !cLower.includes('extraordinaria');
                                                        const esAdeudo = cLower.includes('adeudo') && (cLower.includes('anterior') || cLower.includes('pendiente'));
                                                        if (!(esMant || esAdeudo) || !moduloSeleccionMesesActivo) return null;

                                                        if (pago.mesSeleccionadoTexto) {
                                                            return (
                                                                <div className="badge-mes-elegido" style={{ marginTop: '6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px', fontSize: '0.82rem' }}>
                                                                    <span style={{ color: '#1e40af' }}>
                                                                        📅 Mes a liquidar: <strong>{pago.mesSeleccionadoTexto}</strong> (${Number(pago.mesSeleccionadoMonto || pago.monto || 0).toFixed(2)})
                                                                    </span>
                                                                    <button
                                                                        type="button"
                                                                        style={{ background: 'none', border: 'none', color: '#2563eb', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer', fontSize: '0.8rem' }}
                                                                        onClick={() => {
                                                                            setPartidaParaSeleccionarMes(pago.id);
                                                                            setModalSelectorMesOpen(true);
                                                                        }}
                                                                    >
                                                                        Cambiar mes
                                                                    </button>
                                                                </div>
                                                            );
                                                        }

                                                        if (mesesPendientesData && mesesPendientesData.length > 0) {
                                                            return (
                                                                <div style={{ marginTop: '6px' }}>
                                                                    <button
                                                                        type="button"
                                                                        style={{ background: '#f8fafc', border: '1px dashed #94a3b8', borderRadius: '6px', padding: '5px 10px', fontSize: '0.8rem', color: '#475569', cursor: 'pointer', fontWeight: 600, width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                                                                        onClick={() => {
                                                                            setPartidaParaSeleccionarMes(pago.id);
                                                                            setModalSelectorMesOpen(true);
                                                                        }}
                                                                    >
                                                                        <span>📅 Elegir mes a liquidar ({mesesPendientesData.length} pendientes)</span>
                                                                        <span style={{ color: '#2563eb' }}>Seleccionar ➔</span>
                                                                    </button>
                                                                </div>
                                                            );
                                                        }
                                                        return null;
                                                    })()}
                                                </div>

                                                {(() => {
                                                    const isAguaConcept = String(pago.concepto || '').toLowerCase().includes('agua');
                                                    const isAguaSinPendiente = isAguaConcept && periodosAguaPendientes.length === 0;

                                                    const isCuotaExtra = String(pago.concepto || '').toLowerCase().includes('cuota') && String(pago.concepto || '').toLowerCase().includes('extraordinaria');
                                                    const selectedCuota = isCuotaExtra ? cuotasExtras.find(c => String(c.id) === String(pago.cuotaExtraId)) : null;
                                                    const isExtraPagada = selectedCuota ? isCuotaExtraPagadaCompleta(selectedCuota, rawMesesData) : false;

                                                    const isSancionConcept = esConceptoSancion(pago.concepto);
                                                    const isSancionSinPendiente = isSancionConcept && (!sancionesCasaList || sancionesCasaList.length === 0);

                                                    const isDisabledInput = isAguaSinPendiente || isExtraPagada || isSancionSinPendiente;
                                                    const disabledValue = isDisabledInput ? 0 : pago.monto;

                                                    return (
                                                        <div className="form-group">
                                                            <label className="form-label-row">Monto Asignado ($) <span className="help-icon" title="Monto destinado a este concepto">?</span></label>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                inputMode="decimal"
                                                                pattern="[0-9]*[.,]?[0-9]*"
                                                                value={disabledValue}
                                                                disabled={isDisabledInput}
                                                                onChange={e => {
                                                                    if (isDisabledInput) return;
                                                                    const clean = sanitizeNumberInput(e.target.value, true);
                                                                    handleUpdatePago(pago.id, 'monto', parseFloat(clean) || 0);
                                                                }}
                                                                onBeforeInput={preventNonNumeric}
                                                                placeholder="0.00"
                                                                style={isDisabledInput ? { opacity: 0.6, cursor: 'not-allowed', backgroundColor: '#f1f5f9' } : {}}
                                                                title={isAguaSinPendiente ? "No se registran consumos de agua pendientes para esta casa" : isExtraPagada ? "Esta cuota extraordinaria ya se encuentra pagada completamente" : isSancionSinPendiente ? "Este domicilio no cuenta con sanciones administrativas pendientes asignadas" : ""}
                                                            />
                                                        </div>
                                                    );
                                                })()}
                                            </div>

                                            {/* Sub-selectores condicionales según el concepto */}
                                            {(() => {
                                                const isAguaConcept = String(pago.concepto || '').toLowerCase().includes('agua');
                                                if (!isAguaConcept) return null;
                                                if (periodosAguaPendientes.length === 0) {
                                                    return (
                                                        <div className="form-group" style={{ marginTop: '8px' }}>
                                                            <label className="form-label-row">Período de Agua Pendiente</label>
                                                            <div className="cp-muted">No se registran consumos de agua pendientes para esta casa.</div>
                                                        </div>
                                                    );
                                                }
                                                return (
                                                    <div className="form-group" style={{ marginTop: '8px' }}>
                                                        <label className="form-label-row">Período de Agua Pendiente <span className="help-icon" title="Elige el recibo de agua a liquidar">?</span></label>
                                                        <select
                                                            value={pago.periodoAguaId || ''}
                                                            onChange={(e) => {
                                                                const selectedId = e.target.value;
                                                                const itemPeriodo = periodosAguaPendientes.find(p => p.id === selectedId);
                                                                if (itemPeriodo) {
                                                                    const montoPendiente = Number(itemPeriodo.montoPendiente || 0);
                                                                    handleUpdatePago(pago.id, {
                                                                        periodoAguaId: selectedId,
                                                                        monto: montoPendiente,
                                                                        saldoFavorConsumido: 0,
                                                                        observacionesAuto: `Pago de consumo de agua: ${itemPeriodo.periodoLectura} (${itemPeriodo.consumoM3} m³)`
                                                                    });
                                                                } else {
                                                                    handleUpdatePago(pago.id, {
                                                                        periodoAguaId: '',
                                                                        monto: 0,
                                                                        saldoFavorConsumido: 0,
                                                                        observacionesAuto: ''
                                                                    });
                                                                }
                                                            }}
                                                        >
                                                            <option value="">Seleccionar período a cubrir...</option>
                                                            {periodosAguaPendientes.map((periodo) => {
                                                                const yaSeleccionado = pagos.some(other => other.id !== pago.id && String(other.periodoAguaId) === String(periodo.id));
                                                                return (
                                                                    <option key={periodo.id} value={periodo.id} disabled={yaSeleccionado}>
                                                                        {periodo.periodoLectura} - (${Number(periodo.montoPendiente).toFixed(2)}) {yaSeleccionado ? '— (Ya seleccionado en otra partida)' : ''}
                                                                    </option>
                                                                );
                                                            })}
                                                        </select>
                                                        <small className="cp-muted">El consumo de agua se cobra de forma separada de su saldo a favor de mantenimiento ordinario.</small>
                                                    </div>
                                                );
                                            })()}

                                            {(() => {
                                                const isMultaAdmin = esConceptoSancion(pago.concepto);
                                                if (!isMultaAdmin) return null;

                                                if (!sancionesCasaList || sancionesCasaList.length === 0) {
                                                    return (
                                                        <div className="form-group" style={{ marginTop: '8px' }}>
                                                            <label className="form-label-row">Sanción Administrativa</label>
                                                            <div className="cp-muted" style={{ color: '#be185d', fontWeight: 600 }}>
                                                                Este domicilio no tiene sanciones administrativas pendientes asignadas.
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                                return (
                                                    <div className="form-group" style={{ marginTop: '8px' }}>
                                                        <label className="form-label-row">Sanción Administrativa <span className="help-icon" title="Sanciones pendientes asignadas a este domicilio">?</span></label>
                                                        <select
                                                            value={pago.multaAdminId ?? ''}
                                                            onChange={(e) => handleSelectMulta(e.target.value, pago.id)}
                                                        >
                                                            <option value="">Seleccionar sanción...</option>
                                                            {sancionesCasaList.map((m) => {
                                                                const yaSeleccionada = pagos.some(other => other.id !== pago.id && String(other.multaAdminId) === String(m.id));
                                                                return (
                                                                    <option key={m.id} value={m.id} disabled={yaSeleccionada}>
                                                                        {m.descripcion} ({`$${Number(m.monto || 0).toFixed(2)}`}) {yaSeleccionada ? '— (Ya seleccionada en otra partida)' : ''}
                                                                    </option>
                                                                );
                                                            })}
                                                        </select>
                                                    </div>
                                                );
                                            })()}

                                            {(() => {
                                                const isCuotaExtra = String(pago.concepto || '').toLowerCase().includes('cuota') && String(pago.concepto || '').toLowerCase().includes('extraordinaria');
                                                if (!isCuotaExtra) return null;
                                                if (!cuotasExtras || cuotasExtras.length === 0) {
                                                    return (
                                                        <div className="form-group" style={{ marginTop: '8px' }}>
                                                            <label className="form-label-row">Cuota Extraordinaria</label>
                                                            <div className="cp-muted">No hay cuotas extraordinarias configuradas en Configuración Financiera.</div>
                                                        </div>
                                                    );
                                                }
                                                const selectedCuota = cuotasExtras.find(c => String(c.id) === String(pago.cuotaExtraId));
                                                const isCurrentPagada = selectedCuota ? isCuotaExtraPagadaCompleta(selectedCuota, rawMesesData) : false;

                                                return (
                                                    <div className="form-group" style={{ marginTop: '8px' }}>
                                                        <label className="form-label-row">Cuota Extraordinaria <span className="help-icon" title="Se cargan desde Configuración Financiera">?</span></label>
                                                        <select
                                                            value={pago.cuotaExtraId ?? ''}
                                                            onChange={(e) => handleSelectCuotaExtra(e.target.value, pago.id)}
                                                        >
                                                            <option value="">Seleccionar cuota extraordinaria...</option>
                                                            {cuotasExtras.map((c) => {
                                                                const pagada = isCuotaExtraPagadaCompleta(c, rawMesesData);
                                                                const yaSeleccionada = pagos.some(other => other.id !== pago.id && String(other.cuotaExtraId) === String(c.id));
                                                                const isDisabled = pagada || yaSeleccionada;
                                                                return (
                                                                    <option key={c.id} value={c.id} disabled={isDisabled}>
                                                                        {c.descripcion} ({`$${Number(c.monto || 0).toFixed(2)}`}) {pagada ? '— PAGADA COMPLETA 🔒' : yaSeleccionada ? '— (Ya seleccionada en otra partida)' : ''}
                                                                    </option>
                                                                );
                                                            })}
                                                        </select>
                                                        {isCurrentPagada && (
                                                            <div style={{ color: '#dc2626', fontSize: '0.8rem', fontWeight: 600, marginTop: '4px' }}>
                                                                🔒 Esta cuota extraordinaria ya está pagada completamente y no se permite ingresar monto.
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    ))}
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
                                    <button
                                        type="button"
                                        onClick={handleAddPago}
                                        style={{
                                            background: '#f3e8ff',
                                            color: '#6b21a8',
                                            border: '1px solid #c084fc',
                                            borderRadius: '8px',
                                            padding: '8px 14px',
                                            fontSize: '0.85rem',
                                            fontWeight: 700,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px'
                                        }}
                                    >
                                        <span>+ Agregar otro concepto a este pago</span>
                                    </button>
                                </div>

                                {/* Balanza de Validación en Tiempo Real */}
                                {(() => {
                                    const globalNum = parseFloat(montoTotalCap || 0);
                                    const capturadoVal = globalNum > 0 ? globalNum : totalMonto;
                                    const dif = Math.round((capturadoVal - totalMonto) * 100) / 100;

                                    return (
                                        <div style={{
                                            marginTop: '16px',
                                            padding: '12px',
                                            borderRadius: '8px',
                                            background: dif === 0 ? '#f0fdf4' : dif > 0 ? '#eff6ff' : '#fff7ed',
                                            border: `1px solid ${dif === 0 ? '#bbf7d0' : dif > 0 ? '#bfdbfe' : '#fed7aa'}`,
                                            fontSize: '0.85rem'
                                        }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                                                <span>Total Capturado: <strong>${capturadoVal.toFixed(2)}</strong></span>
                                                <span>Suma Desglose: <strong>${totalMonto.toFixed(2)}</strong></span>
                                            </div>
                                            {dif === 0 && (
                                                <div style={{ color: '#15803d', fontWeight: 600 }}>
                                                    ✓ Monto desglosado exactamente ($0.00 por asignar).
                                                </div>
                                            )}
                                            {dif > 0 && (
                                                <div style={{ color: '#1d4ed8', fontWeight: 600 }}>
                                                    ℹ️ El excedente de <strong>${dif.toFixed(2)}</strong> {adeudoTotal > 0 ? 'se abonará automáticamente a Adeudo de Mantenimiento.' : 'se abonará automáticamente a Saldo a Favor de Mantenimiento.'}
                                                </div>
                                            )}
                                            {dif < 0 && (
                                                <div style={{ color: '#c2410c', fontWeight: 700 }}>
                                                    ⚠️ La suma de partidas (${totalMonto.toFixed(2)}) supera el Monto Total Recibido (${capturadoVal.toFixed(2)}).
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* Card de Observaciones y Comprobante */}
                            {(() => {
                                const leyendaCobertura = generarLeyendaCoberturaPago();

                                return (
                                    <div className="pago-card" style={{ marginTop: '20px', border: '1px solid #cbd5e1', borderRadius: '12px', background: '#f8fafc', padding: '16px' }}>
                                        <div className="pago-header" style={{ marginBottom: '14px', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px' }}>
                                            <h4 style={{ margin: 0, color: '#1e293b', fontSize: '1.05rem', fontWeight: 700 }}>
                                                📝 Observaciones y Comprobante
                                            </h4>
                                        </div>

                                        {leyendaCobertura && (
                                            <div style={{
                                                marginBottom: '14px',
                                                padding: '10px 14px',
                                                borderRadius: '8px',
                                                background: '#eff6ff',
                                                border: '1px solid #bfdbfe',
                                                color: '#1d4ed8',
                                                fontSize: '0.88rem',
                                                fontWeight: 600,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px'
                                            }}>
                                                <span style={{ fontSize: '1.1rem' }}>ℹ️</span>
                                                <span><strong>Detalle de Cobertura de este Pago:</strong> {leyendaCobertura}</span>
                                            </div>
                                        )}

                                        <div className="form-grid form-grid-notes">
                                            <div className="form-group">
                                                <label className="form-label-row">Observaciones Generales <span className="help-icon" title="Notas del pago">?</span></label>
                                                <textarea
                                                    value={observacionesGeneral}
                                                    onChange={e => setObservacionesGeneral(e.target.value)}
                                                    rows="2"
                                                    placeholder={leyendaCobertura ? `Sugerido: ${leyendaCobertura}` : "Anotaciones de la transacción..."}
                                                ></textarea>
                                            </div>

                                            <div className="form-group form-group-file">
                                                <label className="form-label-row">Comprobante (Opcional) <span className="help-icon" title="Imagen o PDF del comprobante">?</span></label>
                                                <div className="custom-file-upload">
                                                    <input
                                                        type="file"
                                                        id="file-upload-general"
                                                        accept="image/*,.pdf"
                                                        style={{ display: 'none' }}
                                                        onChange={e => {
                                                            const file = e.target.files[0];
                                                            setComprobanteGeneral(file);
                                                            setPagos(pagos.map(p => ({ ...p, comprobante: file })));
                                                        }}
                                                    />
                                                    <label htmlFor="file-upload-general" className="file-upload-button">
                                                        <FaUpload aria-hidden="true" />
                                                        <span>{comprobanteGeneral ? 'Cambiar archivo' : 'Examinar...'}</span>
                                                    </label>
                                                    {comprobanteGeneral && (
                                                        <div className="file-name-badge">
                                                            <div className="file-icon-mini">
                                                                {comprobanteGeneral.type.includes('pdf') ? 'PDF' : 'IMG'}
                                                            </div>
                                                            <span title={comprobanteGeneral.name}>{comprobanteGeneral.name}</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <small className="cp-muted">Máximo 5MB • PDF, JPG o PNG</small>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>

                        <aside className="form-column form-column--summary">
                            <div className="tips-card">
                                <p className="tips-title">Ayuda rápida</p>
                                <ul>
                                    <li>Referencia y comprobante son obligatorios.</li>
                                    <li>Revisa que el desglose coincida con el total capturado.</li>
                                    <li>El folio se calcula con la configuración del fraccionamiento.</li>
                                </ul>
                            </div>
                            <div className="summary-card">
                                <p className="summary-label">Resumen rápido</p>
                                <div className="summary-row">
                                    <span>Estado actual</span>
                                    <span style={{ fontWeight: '700', color: adeudoTotalGlobal > 0 ? '#ea580c' : saldoFavorActual > 0 ? '#16a34a' : '#1e40af' }}>
                                        {adeudoTotalGlobal > 0 ? `$${adeudoTotalGlobal.toFixed(2)} (Deuda)` : saldoFavorActual > 0 ? `$${saldoFavorActual.toFixed(2)} (Favor)` : 'Al Corriente'}
                                    </span>
                                </div>
                                {nextFolio && (
                                    <div className="summary-row">
                                        <span>Folio sugerido</span>
                                        <span className="badge badge-soft">{nextFolio}</span>
                                    </div>
                                )}

                                {/* Sugerencia de Intereses Moratorios Pendientes (Requerimiento 5) */}
                                {adeudoInteresesCasa > 0 && (
                                    <div className="banner-intereses-moratorios" style={{ marginTop: '12px', padding: '12px', borderRadius: '10px', background: '#fffbeb', border: '1px solid #fde68a' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#b45309', fontWeight: 700, fontSize: '0.88rem' }}>
                                                <FaBolt style={{ color: '#d97706' }} />
                                                <span>Intereses Moratorios</span>
                                            </div>
                                            <span style={{ fontWeight: 800, color: '#b45309', fontSize: '0.95rem' }}>
                                                ${adeudoInteresesCasa.toFixed(2)}
                                            </span>
                                        </div>
                                        <p style={{ margin: '0 0 8px 0', fontSize: '0.78rem', color: '#78350f', lineHeight: 1.35 }}>
                                            El residente presenta recargos por mora. Se sugiere cubrir primero los intereses con el concepto <strong>"Recargos o intereses"</strong>.
                                        </p>
                                        <button
                                            type="button"
                                            onClick={handleAgregarPartidaIntereses}
                                            style={{
                                                width: '100%',
                                                background: '#fef3c7',
                                                color: '#92400e',
                                                border: '1px solid #f59e0b',
                                                borderRadius: '6px',
                                                padding: '6px 10px',
                                                fontSize: '0.8rem',
                                                fontWeight: 700,
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '6px'
                                            }}
                                         >
                                            <span>+ Liquidar Intereses (${adeudoInteresesCasa.toFixed(2)})</span>
                                        </button>
                                    </div>
                                )}

                                {/* Acordeón de Mantenimiento Pendiente (Requerimiento 3) */}
                                {mesesPendientesData && mesesPendientesData.length > 0 && (
                                    <div className="sidebar-accordion-section" style={{ marginTop: '12px', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden', background: '#ffffff' }}>
                                        <button
                                            type="button"
                                            onClick={() => setAcordeonManttoOpen(!acordeonManttoOpen)}
                                            style={{
                                                width: '100%',
                                                padding: '8px 12px',
                                                background: '#f8fafc',
                                                border: 'none',
                                                borderBottom: acordeonManttoOpen ? '1px solid #e2e8f0' : 'none',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                cursor: 'pointer',
                                                fontSize: '0.85rem',
                                                fontWeight: 700,
                                                color: '#334155'
                                            }}
                                        >
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <FaCalendarAlt style={{ color: '#3b82f6' }} />
                                                Mantenimiento Pendiente ({mesesPendientesData.length})
                                            </span>
                                            <span style={{ color: '#64748b', fontSize: '0.75rem' }}>
                                                {acordeonManttoOpen ? <FaChevronUp /> : <FaChevronDown />}
                                            </span>
                                        </button>
                                        {acordeonManttoOpen && (
                                            <div style={{ padding: '8px 10px', maxHeight: '160px', overflowY: 'auto' }}>
                                                {mesesPendientesData.map((m, idx) => {
                                                    const rawMonto = Number(m.montoPendiente ?? m.monto ?? cuotaMensual);
                                                    const mesNum = parseMesNumero(m.mesNumero || m.mes);
                                                    const mesNombre = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'][mesNum - 1] || m.mes;
                                                    const anioStr = m.año || m.anio;
                                                    const est = String(m.estado || '').toLowerCase();
                                                    const esParcial = est.includes('parcial');
                                                    
                                                    const pagosDelMes = pagos.filter(p => {
                                                        const esMant = esConceptoMantenimiento(p.concepto);
                                                        if (!esMant) return false;
                                                        if (p.mesSeleccionadoId && m.id) {
                                                            return String(p.mesSeleccionadoId) === String(m.id);
                                                        }
                                                        if (p.mesSeleccionadoTexto && p.mesSeleccionadoTexto !== 'Automático (FIFO)') {
                                                            const mesAnioTexto = `${mesNombre} ${anioStr}`.toLowerCase().trim();
                                                            return p.mesSeleccionadoTexto.toLowerCase().trim() === mesAnioTexto;
                                                        }
                                                        return false;
                                                    });

                                                    const montoAsignado = pagosDelMes.reduce((acc, p) => acc + Number(p.monto || 0), 0);
                                                    const asignadoEnPago = pagosDelMes.length > 0 && montoAsignado > 0;
                                                    const esAbono = asignadoEnPago && Math.round(montoAsignado * 100) < Math.round(rawMonto * 100);

                                                    return (
                                                        <div key={m.id || idx} className="summary-row" style={{ fontSize: '0.8rem', padding: '3px 0', borderBottom: '1px dashed #f1f5f9' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                <span style={{ color: '#475569', fontWeight: 500 }}>
                                                                    {mesNombre} {anioStr}
                                                                </span>
                                                                {esParcial && (
                                                                    <span style={{ fontSize: '0.68rem', padding: '1px 4px', background: '#fef3c7', color: '#92400e', borderRadius: '4px', fontWeight: 600 }}>Parcial</span>
                                                                )}
                                                                {asignadoEnPago && (
                                                                    <span style={{ fontSize: '0.68rem', padding: '1px 4px', background: '#dcfce7', color: '#166534', borderRadius: '4px', fontWeight: 700 }}>
                                                                        {esAbono ? `✓ En pago ($${montoAsignado.toFixed(2)} Abonado)` : '✓ En pago'}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <span style={{ fontWeight: 600, color: asignadoEnPago ? '#16a34a' : '#1e293b' }}>
                                                                ${rawMonto.toFixed(2)}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                                <div className="summary-row" style={{ marginTop: '6px', paddingTop: '6px', borderTop: '1px solid #e2e8f0', fontSize: '0.82rem' }}>
                                                    <span style={{ fontWeight: 600, color: '#334155' }}>Total Mantto. Pendiente</span>
                                                    <span style={{ fontWeight: 700, color: '#1d4ed8' }}>
                                                         ${mesesPendientesData.reduce((acc, curr) => acc + Number(curr.montoPendiente ?? curr.monto ?? cuotaMensual), 0).toFixed(2)}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                                 {/* Acordeón de Cuotas Extraordinarias (Requerimiento 3) */}
                                 {cuotasExtras && cuotasExtras.length > 0 && (
                                     <div className="sidebar-accordion-section" style={{ marginTop: '10px', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden', background: '#ffffff' }}>
                                         <button
                                             type="button"
                                             onClick={() => setAcordeonExtrasOpen(!acordeonExtrasOpen)}
                                             style={{
                                                 width: '100%',
                                                 padding: '8px 12px',
                                                 background: '#f8fafc',
                                                 border: 'none',
                                                 borderBottom: acordeonExtrasOpen ? '1px solid #e2e8f0' : 'none',
                                                 display: 'flex',
                                                 alignItems: 'center',
                                                 justifyContent: 'space-between',
                                                 cursor: 'pointer',
                                                 fontSize: '0.85rem',
                                                 fontWeight: 700,
                                                 color: '#334155'
                                             }}
                                         >
                                             <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                 <FaBolt style={{ color: '#8b5cf6' }} />
                                                 Cuotas Extraordinarias ({cuotasExtras.length})
                                             </span>
                                             <span style={{ color: '#64748b', fontSize: '0.75rem' }}>
                                                 {acordeonExtrasOpen ? <FaChevronUp /> : <FaChevronDown />}
                                             </span>
                                         </button>
                                         {acordeonExtrasOpen && (
                                             <div style={{ padding: '8px 10px', maxHeight: '160px', overflowY: 'auto' }}>
                                                 {cuotasExtras.map((cuota, idx) => {
                                                     const pagada = isCuotaExtraPagadaCompleta(cuota, rawMesesData);
                                                     const cuotaMontoPendiente = Number(cuota.monto || 0);
                                                     const pagosDeLaExtra = pagos.filter(p => {
                                                         const esExtra = esConceptoCuotaExtra(p.concepto, p.cuotaExtraId);
                                                         return esExtra && String(p.cuotaExtraId) === String(cuota.id);
                                                     });
                                                     const montoAsignadoExtra = pagosDeLaExtra.reduce((acc, p) => acc + Number(p.monto || 0), 0);
                                                     const asignadaEnPago = pagosDeLaExtra.length > 0 && montoAsignadoExtra > 0;
                                                     const esAbonoExtra = asignadaEnPago && Math.round(montoAsignadoExtra * 100) < Math.round(cuotaMontoPendiente * 100);

                                                     return (
                                                         <div key={cuota.id || idx} className="summary-row" style={{ fontSize: '0.8rem', padding: '3px 0', borderBottom: '1px dashed #f1f5f9' }}>
                                                             <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                 <span style={{ color: pagada ? '#059669' : '#475569', textDecoration: pagada ? 'line-through' : 'none', fontWeight: 500 }}>
                                                                     {cuota.descripcion} {pagada ? '(Pagada)' : ''}
                                                                 </span>
                                                                 {asignadaEnPago && (
                                                                     <span style={{ fontSize: '0.68rem', padding: '1px 4px', background: '#dcfce7', color: '#166534', borderRadius: '4px', fontWeight: 700 }}>
                                                                         {esAbonoExtra ? `✓ En pago ($${montoAsignadoExtra.toFixed(2)} Abonado)` : '✓ En pago'}
                                                                     </span>
                                                                 )}
                                                             </div>
                                                             <span style={{ fontWeight: '600', color: pagada ? '#059669' : asignadaEnPago ? '#16a34a' : '#1f2937' }}>
                                                                 {pagada ? '✓ $0.00' : `$${cuotaMontoPendiente.toFixed(2)}`}
                                                             </span>
                                                         </div>
                                                     );
                                                 })}
                                                 <div className="summary-row" style={{ marginTop: '6px', paddingTop: '6px', borderTop: '1px solid #e2e8f0', fontSize: '0.82rem' }}>
                                                     <span style={{ fontWeight: 600, color: '#334155' }}>Total Cuotas Extra Pendientes</span>
                                                     <span style={{ fontWeight: 700, color: '#7c3aed' }}>
                                                         ${cuotasExtras
                                                             .filter(c => !isCuotaExtraPagadaCompleta(c, rawMesesData))
                                                             .reduce((sum, c) => sum + Number(c.monto || 0), 0)
                                                             .toFixed(2)}
                                                     </span>
                                                 </div>
                                             </div>
                                         )}
                                     </div>
                                 )}

                                 {/* Acordeón de Sanciones Administrativas */}
                                 {sancionesCasaList && sancionesCasaList.length > 0 && (
                                     <div className="sidebar-accordion-section" style={{ marginTop: '10px', border: '1px solid #fbcfe8', borderRadius: '8px', overflow: 'hidden', background: '#ffffff' }}>
                                         <button
                                             type="button"
                                             onClick={() => setAcordeonSancionesOpen(!acordeonSancionesOpen)}
                                             style={{
                                                 width: '100%',
                                                 padding: '8px 12px',
                                                 background: '#fdf2f8',
                                                 border: 'none',
                                                 borderBottom: acordeonSancionesOpen ? '1px solid #fbcfe8' : 'none',
                                                 display: 'flex',
                                                 alignItems: 'center',
                                                 justifyContent: 'space-between',
                                                 cursor: 'pointer',
                                                 fontSize: '0.85rem',
                                                 fontWeight: 700,
                                                 color: '#831843'
                                             }}
                                         >
                                             <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                 <FaExclamationTriangle style={{ color: '#be185d' }} />
                                                 Sanciones Administrativas ({sancionesCasaList.length})
                                             </span>
                                             <span style={{ color: '#be185d', fontSize: '0.75rem' }}>
                                                 {acordeonSancionesOpen ? <FaChevronUp /> : <FaChevronDown />}
                                             </span>
                                         </button>
                                         {acordeonSancionesOpen && (
                                             <div style={{ padding: '8px 10px', maxHeight: '160px', overflowY: 'auto' }}>
                                                 {sancionesCasaList.map((sancion, idx) => {
                                                     const sancionMonto = Number(sancion.monto || 0);
                                                     const pagosDeSancion = pagos.filter(p => {
                                                         const esSanc = esConceptoSancion(p.concepto);
                                                         return esSanc && (String(p.multaAdminId) === String(sancion.id) || p.multaAdminDesc === sancion.descripcion);
                                                     });
                                                     const montoAsignadoSancion = pagosDeSancion.reduce((acc, p) => acc + Number(p.monto || 0), 0);
                                                     const asignadaEnPago = pagosDeSancion.length > 0 && montoAsignadoSancion > 0;
                                                     const esAbono = asignadaEnPago && Math.round(montoAsignadoSancion * 100) < Math.round(sancionMonto * 100);

                                                     return (
                                                         <div key={sancion.id || idx} className="summary-row" style={{ fontSize: '0.8rem', padding: '3px 0', borderBottom: '1px dashed #fce7f3' }}>
                                                             <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                 <span style={{ color: '#831843', fontWeight: 500 }}>
                                                                     {sancion.descripcion}
                                                                 </span>
                                                                 {asignadaEnPago && (
                                                                     <span style={{ fontSize: '0.68rem', padding: '1px 4px', background: '#dcfce7', color: '#166534', borderRadius: '4px', fontWeight: 700 }}>
                                                                         {esAbono ? `✓ En pago ($${montoAsignadoSancion.toFixed(2)})` : '✓ En pago'}
                                                                     </span>
                                                                 )}
                                                             </div>
                                                             <span style={{ fontWeight: '600', color: asignadaEnPago ? '#16a34a' : '#be185d' }}>
                                                                 ${sancionMonto.toFixed(2)}
                                                             </span>
                                                         </div>
                                                     );
                                                 })}
                                                 <div className="summary-row" style={{ marginTop: '6px', paddingTop: '6px', borderTop: '1px solid #fbcfe8', fontSize: '0.82rem' }}>
                                                     <span style={{ fontWeight: 600, color: '#831843' }}>Total Sanciones Pendientes</span>
                                                     <span style={{ fontWeight: 700, color: '#be185d' }}>
                                                         ${sancionesCasaList.reduce((sum, s) => sum + Number(s.monto || 0), 0).toFixed(2)}
                                                     </span>
                                                 </div>
                                             </div>
                                         )}
                                     </div>
                                 )}

                                {/* Sección de Distribución del Monto Capturado con colores distintivos */}
                                <div className="divider" aria-hidden="true"></div>
                                {(() => {
                                    const globalNum = parseFloat(montoTotalCap || 0);
                                    const capturadoVal = globalNum > 0 ? globalNum : totalMonto;
                                    const excedenteFavor = Math.max(0, Math.round((capturadoVal - totalMonto) * 100) / 100);

                                    // Función auxiliar para asignar color y etiqueta según el concepto
                                    const getConceptoBadge = (conceptoStr) => {
                                        const lower = String(conceptoStr || '').toLowerCase();
                                        if (lower.includes('agua') || lower.includes('cea')) {
                                            return { label: 'Agua Potable', color: '#0e7490', bg: '#ecfeff', border: '#a5f3fc' };
                                        }
                                        if (lower.includes('extraordinaria') || lower.includes('cuota extra')) {
                                            return { label: 'Cuota Extraordinaria', color: '#6d28d9', bg: '#f5f3ff', border: '#ddd6fe' };
                                        }
                                        if (esConceptoSancion(conceptoStr)) {
                                            return { label: 'Sanción Admin', color: '#be185d', bg: '#fce7f3', border: '#fbcfe8' };
                                        }
                                        if (lower.includes('recargo') || lower.includes('interes')) {
                                            return { label: 'Recargos o intereses', color: '#b45309', bg: '#fffbeb', border: '#fde68a' };
                                        }
                                        if (esConceptoMantenimiento(conceptoStr)) {
                                            return { label: 'Cuota de Mantto', color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe' };
                                        }
                                        return { label: '💼 Ingreso de Tesorería', color: '#b45309', bg: '#fffbeb', border: '#fde68a' };
                                    };

                                    return (
                                        <>
                                            <div className="summary-row" style={{ fontWeight: 700 }}>
                                                <span>Total capturado</span>
                                                <span style={{ fontSize: '1.05rem', color: '#0f172a' }}>${capturadoVal.toFixed(2)}</span>
                                            </div>

                                            {/* Desglose visual por conceptos desglosados */}
                                            <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                {pagos.map((p, idx) => {
                                                    const badge = getConceptoBadge(p.concepto);
                                                    const itemMonto = parseFloat(p.monto || 0);
                                                    return (
                                                        <div key={p.id || idx} className="summary-row" style={{ fontSize: '0.85rem', padding: '6px 8px', borderRadius: '6px', background: badge.bg, border: `1px solid ${badge.border}` }}>
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', textAlign: 'left' }}>
                                                                <span style={{ color: badge.color, fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                    <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: badge.color }}></span>
                                                                    {p.concepto || `Partida #${idx + 1}`}
                                                                </span>
                                                                {(() => {
                                                                    let detalleIdentificador = '';
                                                                    const cLower = String(p.concepto || '').toLowerCase();
                                                                    if (cLower.includes('cuota') && cLower.includes('mantenimiento') && !cLower.includes('extraordinaria')) {
                                                                        if (p.mesSeleccionadoTexto) {
                                                                            detalleIdentificador = p.mesSeleccionadoTexto;
                                                                        } else if (p.observacionesAuto && p.observacionesAuto.includes('Cuota de mantenimiento:')) {
                                                                            detalleIdentificador = p.observacionesAuto.replace('Cuota de mantenimiento:', '').trim();
                                                                        }
                                                                    } else if (cLower.includes('extraordinaria')) {
                                                                        detalleIdentificador = p.cuotaExtraDesc || '';
                                                                    } else if (cLower.includes('agua') || cLower.includes('cea')) {
                                                                        const pAgua = periodosAguaPendientes.find(a => a.id === p.periodoAguaId);
                                                                        if (pAgua) detalleIdentificador = pAgua.periodoLectura;
                                                                    } else if (cLower.includes('multa')) {
                                                                        detalleIdentificador = p.multaAdminDesc || '';
                                                                    } else if (cLower.includes('recargo') || cLower.includes('interes')) {
                                                                        detalleIdentificador = 'Mora amortizada';
                                                                    }
                                                                    return detalleIdentificador ? (
                                                                        <span style={{ fontSize: '0.78rem', color: '#475569', paddingLeft: '14px', fontWeight: 600 }}>
                                                                            ↳ {detalleIdentificador}
                                                                        </span>
                                                                    ) : null;
                                                                })()}
                                                            </div>
                                                            <span style={{ fontWeight: 700, color: badge.color }}>${itemMonto.toFixed(2)}</span>
                                                        </div>
                                                    );
                                                })}

                                                {excedenteFavor > 0 && (
                                                    <div className="summary-row" style={{ fontSize: '0.85rem', padding: '4px 8px', borderRadius: '6px', background: adeudoTotal > 0 ? '#eff6ff' : '#ecfdf5', border: `1px solid ${adeudoTotal > 0 ? '#bfdbfe' : '#a7f3d0'}` }}>
                                                        <span style={{ color: adeudoTotal > 0 ? '#1d4ed8' : '#047857', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                            <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: adeudoTotal > 0 ? '#1d4ed8' : '#047857' }}></span>
                                                            {adeudoTotal > 0 ? 'Cuota de Mantenimiento' : 'Cuota de Mantenimiento (Saldo a Favor)'}
                                                        </span>
                                                        <span style={{ fontWeight: 700, color: adeudoTotal > 0 ? '#1d4ed8' : '#047857' }}>${excedenteFavor.toFixed(2)}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    );
                                })()}

                                {/* Tarjeta Destacada Final: Total a Aplicar y Adeudo o Saldo Favor Final */}
                                <div style={{ marginTop: '16px', background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: '10px', padding: '14px' }}>
                                    <div className="summary-row" style={{ marginBottom: '8px' }}>
                                        <span style={{ fontWeight: 700, color: '#1e3a8a' }}>Total a aplicar</span>
                                        <span style={{ fontWeight: 800, fontSize: '1.1rem', color: '#1d4ed8' }}>${totalMonto.toFixed(2)}</span>
                                    </div>
                                    <div className="divider" style={{ margin: '8px 0', borderColor: '#bfdbfe' }}></div>
                                    {(() => {
                                        const globalNum = parseFloat(montoTotalCap || 0);
                                        const capturadoTotal = globalNum > 0 ? globalNum : totalMonto;

                                        // Separar lo que realmente aplica a adeudos de la casa (Categoría A) vs Tesorería pura (Categoría B)
                                        const montoAplicableAdeudo = pagos
                                            .filter(p => esPartidaContableResidente(p))
                                            .reduce((sum, p) => {
                                                const m = parseFloat(p.monto || 0);
                                                const d = m * (parseFloat(p.descuento || 0) / 100);
                                                return sum + Math.max(0, m - d);
                                            }, 0);
                                        
                                        const montoTesoreria = pagos
                                            .filter(p => !esPartidaContableResidente(p))
                                            .reduce((sum, p) => sum + parseFloat(p.montoNeto || p.monto || 0), 0);

                                        let saldoFinalTexto = '';
                                        let saldoFinalColor = '#1e3a8a';

                                        if (adeudoTotalGlobal > 0) {
                                            const deudafinal = Math.max(0, Math.round((adeudoTotalGlobal - montoAplicableAdeudo) * 100) / 100);
                                            if (deudafinal > 0) {
                                                saldoFinalTexto = `$${deudafinal.toFixed(2)} (Deuda)`;
                                                saldoFinalColor = '#c2410c';
                                            } else if (montoAplicableAdeudo > adeudoTotalGlobal) {
                                                const sobrante = Math.round((montoAplicableAdeudo - adeudoTotalGlobal + saldoFavorActual) * 100) / 100;
                                                saldoFinalTexto = `$${sobrante.toFixed(2)} (Favor)`;
                                                saldoFinalColor = '#15803d';
                                            } else {
                                                saldoFinalTexto = 'Al Corriente';
                                                saldoFinalColor = '#15803d';
                                            }
                                        } else if (saldoFavorActual > 0) {
                                            const favorFinal = Math.round((saldoFavorActual + montoAplicableAdeudo) * 100) / 100;
                                            saldoFinalTexto = `$${favorFinal.toFixed(2)} (Favor)`;
                                            saldoFinalColor = '#15803d';
                                        } else {
                                            if (montoAplicableAdeudo > 0) {
                                                saldoFinalTexto = `$${montoAplicableAdeudo.toFixed(2)} (Favor)`;
                                                saldoFinalColor = '#15803d';
                                            } else {
                                                saldoFinalTexto = 'Al Corriente';
                                                saldoFinalColor = '#15803d';
                                            }
                                        }

                                        return (
                                            <>
                                                {montoTesoreria > 0 && (
                                                    <div className="summary-row" style={{ marginTop: '4px', fontSize: '0.85rem' }}>
                                                        <span style={{ color: '#b45309', fontWeight: 600 }}>💼 Ingreso a Tesorería</span>
                                                        <span style={{ fontWeight: 700, color: '#b45309' }}>${montoTesoreria.toFixed(2)} (No amortiza adeudo)</span>
                                                    </div>
                                                )}
                                                <div className="summary-row" style={{ marginTop: '4px' }}>
                                                    <span style={{ fontWeight: 700, color: '#1e3a8a' }}>Adeudo o Saldo Favor Final</span>
                                                    <span style={{ fontWeight: 800, fontSize: '1.15rem', color: saldoFinalColor }}>{saldoFinalTexto}</span>
                                                </div>
                                            </>
                                        );
                                    })()}
                                </div>
                            </div>
                        </aside>
                    </div>

                    <div className="modal-actions-container" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {hasBlockingFormError && (
                            <div className="form-error-message" style={{ margin: 0, padding: '12px', background: '#fff7ed', border: '1px solid #ffedd5', color: '#9a3412', borderRadius: '8px', fontSize: '0.85rem' }}>
                                <FaExclamationTriangle style={{ color: '#ea580c' }} /> <strong>Atención Admin:</strong> {blockingError.message} Por favor, revisa la <strong>Configuración Financiera</strong>.
                            </div>
                        )}

                        {(() => {
                            const globalNumCap = parseFloat(montoTotalCap || 0);
                            const isSumaExcedida = globalNumCap > 0 && Math.round(totalMonto * 100) / 100 > Math.round(globalNumCap * 100) / 100;
                            const isMontoZero = totalMonto <= 0 && globalNumCap <= 0;
                            const isDesgloseInvalido = isSumaExcedida || isMontoZero;

                            return (
                                <>
                                    {isSumaExcedida && (
                                        <div className="form-error-message" style={{ margin: 0, padding: '12px', background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 600 }}>
                                            <FaExclamationTriangle style={{ color: '#dc2626' }} /> <strong>Desglose Inválido:</strong> La suma de las partidas (${totalMonto.toFixed(2)}) supera el Monto Total Recibido (${globalNumCap.toFixed(2)}). Ajusta los montos para poder confirmar el pago.
                                        </div>
                                    )}

                                    {errorMessage && (
                                        <div className="form-error-message" style={{ margin: 0, padding: '12px', background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 600 }}>
                                            <FaExclamationTriangle style={{ color: '#dc2626' }} /> {errorMessage}
                                        </div>
                                    )}

                                    <div className="modal-actions" style={{ marginTop: 0 }}>
                                        <button type="button" onClick={handleRequestClose} className="btn-cancel" disabled={isSubmitting}>Cancelar</button>
                                        <button 
                                            type="submit" 
                                            className="btn-confirm" 
                                            disabled={isSubmitting || loadingConfig || hasBlockingFormError || isDesgloseInvalido}
                                            style={isDesgloseInvalido ? { opacity: 0.5, cursor: 'not-allowed', backgroundColor: '#94a3b8', borderColor: '#64748b' } : {}}
                                            title={
                                                hasBlockingFormError ? blockingError.message :
                                                isSumaExcedida ? `La suma de partidas ($${totalMonto.toFixed(2)}) supera el Monto Total Recibido ($${globalNumCap.toFixed(2)})` :
                                                isMontoZero ? "El monto a pagar debe ser mayor a $0.00" : ""
                                            }
                                        >
                                            {isSubmitting ? 'Procesando...' : 'Confirmar Pago'}
                                        </button>
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                </form>
            </div>

            {/* Modal Emergente y Sutil para Seleccionar Mes de Adeudo (Requerimiento 1) */}
            {modalSelectorMesOpen && (
                <div className="selector-mes-backdrop">
                    <div className="selector-mes-modal">
                        <div className="selector-mes-header">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '1.25rem' }}>📅</span>
                                <div>
                                    <h4 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: '#1e293b' }}>
                                        Seleccionar Mes de Adeudo a Liquidar
                                    </h4>
                                    <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                                        Casa {casa?.numero || casa?.id} • {nombrePropietario}
                                    </span>
                                </div>
                            </div>
                            <button
                                type="button"
                                className="selector-mes-close-btn"
                                onClick={() => {
                                    setModalSelectorMesOpen(false);
                                    setPartidaParaSeleccionarMes(null);
                                }}
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <div className="selector-mes-body">
                            {/* Opción rápida automática (FIFO) */}
                            <div
                                className="selector-mes-option-auto"
                                onClick={() => handleSeleccionarMesParaPartida(null)}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ fontSize: '1.2rem' }}>🔄</span>
                                    <div>
                                        <div style={{ fontWeight: 700, color: '#1d4ed8', fontSize: '0.9rem' }}>
                                            Orden cronológico automático (FIFO)
                                        </div>
                                        <div style={{ fontSize: '0.78rem', color: '#4b5563' }}>
                                            Aplica el pago al mes más antiguo adeudado primero.
                                        </div>
                                    </div>
                                </div>
                                <span className="badge-auto-btn">Usar automático</span>
                            </div>

                            <div className="selector-mes-divider">
                                <span>O elige un mes específico de la lista:</span>
                            </div>

                            <div className="selector-mes-list">
                                {mesesPendientesData && mesesPendientesData.length > 0 ? (
                                    mesesPendientesData.map((m) => {
                                        const mesNum = parseMesNumero(m.mesNumero || m.mes);
                                        const mesNombre = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'][mesNum - 1] || m.mes;
                                        const anioStr = m.año || m.anio;
                                        const montoPend = Number(m.montoPendiente ?? m.monto ?? cuotaMensual);
                                        const cuotaBase = Number(m.cuotaMensual ?? cuotaMensual);
                                        const est = String(m.estado || '').toLowerCase();
                                        const esParcial = est.includes('parcial');
                                        const yaAsignadoEnOtra = pagos.some(other => other.id !== partidaParaSeleccionarMes && other.mesSeleccionadoId === m.id);

                                        return (
                                            <div
                                                key={m.id}
                                                className={`selector-mes-item ${yaAsignadoEnOtra ? 'item-disabled' : ''}`}
                                                onClick={() => {
                                                    if (yaAsignadoEnOtra) return;
                                                    handleSeleccionarMesParaPartida({
                                                        id: m.id,
                                                        mesNumero: mesNum,
                                                        mesNombre,
                                                        anio: anioStr,
                                                        montoPendiente: montoPend
                                                    });
                                                }}
                                            >
                                                <div className="selector-mes-item-left">
                                                    <div className="selector-mes-item-title">
                                                        <span>{mesNombre} {anioStr}</span>
                                                        {esParcial && (
                                                            <span className="badge-chip-parcial">Pago Parcial</span>
                                                        )}
                                                        {yaAsignadoEnOtra && (
                                                            <span className="badge-chip-asignado">Ya asignado</span>
                                                        )}
                                                    </div>
                                                    <div className="selector-mes-item-sub">
                                                        {esParcial ? (
                                                            <span>Resta <strong>${montoPend.toFixed(2)}</strong> de una cuota de ${cuotaBase.toFixed(2)}</span>
                                                        ) : (
                                                            <span>Cuota regular de mantenimiento</span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="selector-mes-item-right">
                                                    <span className="selector-mes-item-monto">${montoPend.toFixed(2)}</span>
                                                    <button
                                                        type="button"
                                                        className="btn-select-mes"
                                                        disabled={yaAsignadoEnOtra}
                                                    >
                                                        {yaAsignadoEnOtra ? 'Asignado' : 'Seleccionar'}
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div style={{ textAlign: 'center', padding: '24px 12px', color: '#64748b' }}>
                                        <p style={{ margin: 0, fontWeight: 600 }}>El residente se encuentra al corriente (sin meses adeudados).</p>
                                        <small>Se aplicará la cuota vigente de mantenimiento como anticipo.</small>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="selector-mes-footer">
                            <button
                                type="button"
                                className="btn-cancel"
                                onClick={() => {
                                    setModalSelectorMesOpen(false);
                                    setPartidaParaSeleccionarMes(null);
                                }}
                            >
                                Cerrar sin cambiar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showConfirmClose && (
                <div className="confirm-leave-backdrop">
                    <div className="confirm-leave-modal">
                        <h4>¿Cerrar el formulario?</h4>
                        <p>Los datos capturados se perderán y tendrás que volver a empezar.</p>
                        <div className="confirm-leave-actions">
                            <button type="button" className="btn-secondary" onClick={cancelCloseModal}>Seguir editando</button>
                            <button type="button" className="btn-danger" onClick={confirmCloseModal}>Salir y descartar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ModalRegistroPago;
